/* YU SEI Account Build 02.9 — existing-session recognition for account links.
 * Based on the user's 02.8 account-destination script; retain the 02.4 panel files.
 * On account entry, check Shopify once with prompt=none even in a fresh tab.
 * A missing per-tab resume hint is NOT proof that Shopify has signed the buyer out.
 * A successful sign-in in another same-origin tab can trigger a fresh Shopify
 * check in a visible, signed-out account tab. No tokens or customer identities
 * are sent between tabs. Existing customer/order ownership checks are retained.
 * No custom timeout, persistent token, server session or expiry-policy change.
 * A specific return link still opens its order's returns and focuses that card.
 * No notification template, checkout configuration, Headless setting or data edit.
 * Prepared 23 September 2026. Live Shopify acceptance testing remains required.
 * Sources: https://shopify.dev/docs/api/customer/latest#authentication
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
 * https://developer.mozilla.org/en-US/docs/Web/API/Broadcast_Channel_API
 */
/* YU SEI Account Build 02.7 — order discount breakdown, 22 September 2026.
 * Based on the working 02.6 return wording and 02.4 account-panel synchronisation.
 * Adds ONE read-only pricing query on order-detail expansion. Existing account,
 * fulfilment, return and editing queries/mutations are unchanged.
 * Reads actual line/shipping discount allocations and discount titles/codes.
 * Never infers a discount from a missing amount, a refund or a change in total.
 * Only presents an additive pre-discount subtotal when all loaded lines and
 * allocations reconcile EXACTLY to Shopify's reported subtotal in one currency.
 * Otherwise preserves Shopify's subtotal and shows a separate recorded-discounts
 * section. The order total always comes from Shopify; nothing is written back.
 * Uses bounded six-decimal BigInt arithmetic; unsupported values fail closed to
 * the recorded subtotal display rather than being silently rounded or coerced.
 * Pricing failure does not disable the existing order details, returns or labels.
 * All 02.6 return labels are preserved. No theme CSS/layout or settings changed.
 * Keep Sanctuary content OFF and use the unpublished draft. Customer saves may
 * remain ON for controlled pre-launch testing, as previously agreed.
 * References reviewed 22 September 2026:
 * https://shopify.dev/docs/api/customer/latest/objects/Order
 * https://shopify.dev/docs/api/customer/latest/objects/LineItem
 * https://shopify.dev/docs/api/customer/latest/objects/DiscountAllocation
 * https://shopify.dev/docs/api/customer/latest/interfaces/DiscountApplication
 *
 * Shopify OAuth authorization-code + PKCE browser client, not a general-purpose
 * OpenID Connect ID-token validator or a replacement identity provider.
 * Customer identity is obtained ONLY from the authenticated Customer Account API
 * `customer { id }` response. No ID-token `sub`, email, Liquid customer, URL value,
 * browser storage value, or locally invented ID is used to identify the customer.
 * The token-endpoint response alone NEVER opens a customer-account session.
 * Every new/renewed access token must resolve a customer ID; renewals must resolve
 * the SAME ID before the new token is used by account queries or mutations.
 *
 * Retains direct HTTPS token-response provenance, PKCE, one-time state, nonce,
 * issuer, audience, time and optional token/code hash-binding checks. No JWKS fetch,
 * no CORS bypass, no new external service and no credentials in logs/Web Storage.
 * The claim checks here bind a direct Shopify login response to its transaction;
 * they do not assert full OIDC conformance for an ID token without a string sub.
 * Access/refresh/ID tokens and the bound customer ID remain in memory only.
 * PKCE verifier/state/nonce use sessionStorage for one ten-minute sign-in attempt.
 * Shopify remains the authority for API permissions and customer data isolation.
 *
 * References reviewed 22 September 2026:
 * https://shopify.dev/docs/api/customer/latest#authentication
 * https://shopify.dev/docs/api/customer/latest/queries/customer
 * https://github.com/Shopify/hydrogen/blob/main/packages/hydrogen/src/customer/customer.ts
 * PUBLIC / WEB client only. Never add an Admin token or client secret.
 */
(() => {
  'use strict';
  const configNode = document.getElementById('YuAccountConfig');
  if (!configNode || window.__yuAccountApi02) return;
  window.__yuAccountApi02 = true;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const space = document.querySelector('[data-yu-account]');
  if (!space) return;
  space.dataset.yuAuthBuild = '02.9-account-link-sign-in';
  const setupSummary = space.querySelector('.yu-account-setup > summary');
  if (setupSummary && config.previewNotice) {
    const uiReady = document.documentElement.dataset.yuMemberUiBuild === '02.4' &&
      document.querySelector('yu-member-drawer[data-yu-drawer-build="02.4"]');
    setupSummary.textContent = uiReady ? 'BUILD 02.9 · Account-link sign-in' :
      'BUILD 02.9 · The 02.4 panel-sync files are still required';
  }
  // Presentation only: no tokens, customer IDs, names or emails cross this bridge.
  // Shopify's verified API session remains the sole authority for account data.
  function publishAccountPresentation(state) {
    document.dispatchEvent(new CustomEvent('yu:account-presentation', {
      detail: Object.freeze({ state, clientId: config.clientId })
    }));
  }
  const $ = s => space.querySelector(s);
  const TX = 'yu-account-pkce-02', RESUME = 'yu-account-resume-02', LOGOUT = 'yu-account-logout-02';
  const SIGNOUT_INTENT='yu-account-signout-intent-01', SIGNOUT_INTENT_TTL=120000;
  let signoutIntent=null;
  let auth = null, api = null, session = null, customer = null, orders = [], pageInfo = null;
  let generation = 0, serial = 0, discoveryPromise, refreshPromise, authenticating = false;
  let ordersLoadPromise = null, ordersLoadAppend = false, addressCreateNeedsRefresh = false;
  // Local flow guards, not a new customer-session lifetime or credential store.
  let startupSettled=false, pageLeaving=false, peerReconnectPending=false;
  let lastPeerNoticeAt=0, lastExplicitLogoutAt=0;
  // Preference writes are explicit only; no consent is inferred from sign-in.
  let marketingWrite=null, marketingNeedsRefresh=false, marketingNotice='', marketingRevision=0;
  let nameSaveNotice='';
  let activeAddressModal=null;

  const inFlight = new Set();
  let logoutChannel;
  try { logoutChannel = new BroadcastChannel('yu-account-session-02'); } catch (_) {}
  const addressFields = 'id firstName lastName company address1 address2 city zip territoryCode zoneCode phoneNumber formatted(withName: true)';
  const orderSummary = 'id name createdAt cancelledAt financialStatus fulfillmentStatus totalPrice {amount currencyCode} lineItems(first: 3) {nodes {id name title quantity image {url altText}}} fulfillments(first:20) {nodes {id trackingInformation {company number url}} pageInfo {hasNextPage}}';
  const Q = {
    // Customer-scoped lookup, not a global unauthenticated order lookup.
    linkedOrder: `query YuLinkedOrder($query: String!) { customer { id orders(first: 2, query: $query) { nodes { id name } pageInfo { hasNextPage } } } }`,
    // Same customer-authorised return/refund fields as Q.detail; no eligibility or writes.
    returnOverview: `query YuReturnOverview($id: ID!) { order(id: $id) {
      id name
      returns(first:20) { nodes { id name status createdAt
        returnLineItems(first:20) {nodes {id quantity lineItem {id name title variantTitle}} pageInfo {hasNextPage}}
        reverseDeliveries(first:20) {nodes {id deliverable {... on ReverseDeliveryShippingDeliverable {
          label {publicFileUrl} tracking {carrierName trackingNumber trackingUrl}
        }}} pageInfo {hasNextPage}}
      } pageInfo {hasNextPage}}
      refunds {id createdAt returnName totalRefunded {amount currencyCode}}
      transactions {id kind status createdAt transactionAmount {presentmentMoney {amount currencyCode}}}
    } }`,
    customer: `query YuCustomer { customer {id firstName lastName displayName emailAddress {emailAddress marketingState} defaultAddress {id} addresses(first: 100) {nodes {${addressFields}} pageInfo {hasNextPage endCursor}} } }`,
    addresses: `query YuMoreAddresses($after: String) { customer {addresses(first:100,after:$after) {nodes {${addressFields}} pageInfo {hasNextPage endCursor}}}}`,
    orders: `query YuOrders($after: String) { customer {orders(first: 20, after: $after, reverse: true) {nodes {${orderSummary}} pageInfo {hasNextPage endCursor}}} }`,
    detail: `
      query YuOrder($id: ID!) {
        order(id: $id) {
          id name createdAt cancelledAt financialStatus fulfillmentStatus subtotal {
            amount currencyCode
          }
          totalPrice {
            amount currencyCode
          }
          totalShipping {
            amount currencyCode
          }
          totalTax {
            amount currencyCode
          }
          shippingAddress {
            formatted(withName: true)
          }
          lineItems(first:100) {
            nodes {
              id name title variantTitle quantity image {url altText} totalPrice {
                amount currencyCode
              }
            }
            pageInfo {
              hasNextPage endCursor
            }
          }
          fulfillments(first:20) {
            nodes {
              id estimatedDeliveryAt latestShipmentStatus status trackingInformation {
                company number url
              }
              fulfillmentLineItems(first:20) {
                nodes {
                  id quantity lineItem {id name title variantTitle}
                }
                pageInfo {hasNextPage}
              }
              events(first:30) {
                nodes {
                  id happenedAt status
                }
                pageInfo {hasNextPage}
              }
            }
            pageInfo {
              hasNextPage
            }
          }
          refunds {
            id createdAt returnName totalRefunded {
              amount currencyCode
            }
          }
          transactions {
            id kind status createdAt
            transactionAmount {presentmentMoney {amount currencyCode}}
          }
          returns(first:20) {
            nodes {
              id name status createdAt
              returnLineItems(first:20) {
                nodes {
                  id quantity lineItem {id name title variantTitle}
                }
                pageInfo {hasNextPage}
              }
              reverseDeliveries(first:20) {
                nodes {
                  id deliverable {
                    ... on ReverseDeliveryShippingDeliverable {
                      label {
                        publicFileUrl
                      }
                      tracking {
                        carrierName trackingNumber trackingUrl
                      }
                    }
                  }
                }
                pageInfo {hasNextPage}
              }
            }
            pageInfo {
              hasNextPage
            }
          }
        }
      }
    `,
    moreLines: `query YuOrderLines($id:ID!,$after:String) {order(id:$id) {lineItems(first:100,after:$after) {nodes {id name title variantTitle quantity image {url altText} totalPrice {amount currencyCode}} pageInfo {hasNextPage endCursor}}}}`,
    eligibility: `query YuReturnEligibility($id:ID!) { order(id:$id) {id name returnInformation {hasRestockingFee hasReturnShippingFee returnShippingMethodNote returnableLineItems(first:100) {nodes {quantity lineItem {id name title variantTitle suggestedReturnReasonDefinitions(first:40) {nodes {id name handle deleted} pageInfo {hasNextPage}}}} pageInfo {hasNextPage}} } } }`,
    // Isolated from Q.detail: a pricing-only failure must not block returns/labels.
    pricing: `
      query YuOrderPricing($id: ID!, $after: String) {
        order(id: $id) {
          id updatedAt edited
          subtotal { amount currencyCode }
          totalPrice { amount currencyCode }
          totalShipping { amount currencyCode }
          totalTax { amount currencyCode }
          totalDuties { amount currencyCode }
          totalTip { amount currencyCode }
          shippingLine { title originalPrice { amount currencyCode } }
          shippingDiscountAllocations { allocatedAmount { amount currencyCode }
                discountApplication {
                  __typename targetType
                  ... on ManualDiscountApplication { title }
                  ... on AutomaticDiscountApplication { title }
                  ... on DiscountCodeApplication { code }
                  ... on ScriptDiscountApplication { title }
                } }
          lineItems(first: 100, after: $after) {
            nodes {
              id quantity totalPrice { amount currencyCode }
              discountAllocations { allocatedAmount { amount currencyCode }
                discountApplication {
                  __typename targetType
                  ... on ManualDiscountApplication { title }
                  ... on AutomaticDiscountApplication { title }
                  ... on DiscountCodeApplication { code }
                  ... on ScriptDiscountApplication { title }
                } }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      }
    `,
    marketingSubscribe: `mutation YuEmailSubscribe {customerEmailMarketingSubscribe {emailAddress {marketingState} userErrors {field message}}}`,
    marketingUnsubscribe: `mutation YuEmailUnsubscribe {customerEmailMarketingUnsubscribe {emailAddress {marketingState} userErrors {field message}}}`,
    name: `mutation YuName($input:CustomerUpdateInput!) {customerUpdate(input:$input) {customer {id firstName lastName} userErrors {field message}}}`,
    addAddress: `mutation YuAddAddress($address:CustomerAddressInput!,$defaultAddress:Boolean) {customerAddressCreate(address:$address,defaultAddress:$defaultAddress) {customerAddress {id} userErrors {field message}}}`,
    editAddress: `mutation YuEditAddress($addressId:ID!,$address:CustomerAddressInput,$defaultAddress:Boolean) {customerAddressUpdate(addressId:$addressId,address:$address,defaultAddress:$defaultAddress) {customerAddress {id} userErrors {field message}}}`,
    deleteAddress: `mutation YuDeleteAddress($addressId:ID!) {customerAddressDelete(addressId:$addressId) {deletedAddressId userErrors {field message}}}`,
    return: `mutation YuReturn($orderId:ID!,$requestedLineItems:[RequestedLineItemInput!]!) {orderRequestReturn(orderId:$orderId,requestedLineItems:$requestedLineItems) {return {id name status} userErrors {field message}}}`
  };
  function node(tag, text, cls) { const el = document.createElement(tag); if (text !== undefined && text !== null) el.textContent = String(text); if (cls) el.className = cls; return el; }
  function btn(text, action, cls = 'yu-member-text-button') { const b = node('button', text, cls); b.type = 'button'; if (action) b.addEventListener('click', action); return b; }
  function paragraph(text, cls='yu-member-copy') { return node('p', text, cls); }
  function itemName(line) {return line?.title||line?.name||'Item';}
  function heading(text, level=3) { return node(`h${level}`, text, 'yu-member-title'); }
  function money(value) { if (!value || !Number.isFinite(Number(value.amount))) return 'Not available'; try { return new Intl.NumberFormat(config.locale || 'en-GB', {style:'currency', currency:value.currencyCode}).format(Number(value.amount)); } catch (_) {return `${value.amount} ${value.currencyCode || ''}`;} }
  function date(value, withTime=false) { if (!value) return ''; const d = new Date(value); if (!Number.isFinite(d.getTime())) return ''; return new Intl.DateTimeFormat('en-GB', {day:'numeric', month:'short', year:'numeric', ...(withTime ? {hour:'2-digit',minute:'2-digit'} : {})}).format(d); }
  function human(value) { return typeof value === 'string' ? value.toLowerCase().replace(/_/g,' ').replace(/^./,s=>s.toUpperCase()) : 'Not available'; }
  function status(text, error=false) { const el = $('#YuAccountStatus'); if (!el) return; el.hidden=!text; el.textContent=text || ''; el.classList.toggle('yu-account-notice--error',error); el.dataset.yuRoutineSignin=String(!error && text==='Sign in to securely access your account tools.'); }
  function helpLink(text='Contact YŪ SEI', kind='Order enquiry', order='') { const a=node('a',text,'yu-member-text-link'); a.href='#help'; a.dataset.yuMemberGoto='help'; a.dataset.yuHelpKind=kind; a.dataset.yuHelpOrder=order; return a; }
  function external(text, value) { try {const u=new URL(value); if(u.protocol!=='https:'&&u.protocol!=='http:') return null; const a=node('a',text,'yu-member-text-link'); a.href=u.href; a.target='_blank'; a.rel='noopener noreferrer'; return a;}catch(_){return null;} }
  function appendLink(parent, text, url) {const link=external(text,url); if(link) parent.append(link);}
  function secureUrl(value) { const u=new URL(value); if(u.protocol!=='https:'||u.username||u.password||u.hash) throw new Error('An HTTPS account endpoint is required.'); return u; }
  function store(key,value) { try { if(value===null) sessionStorage.removeItem(key); else sessionStorage.setItem(key,JSON.stringify(value)); return true;}catch(_){return false;} }
  function stored(key) {try{return JSON.parse(sessionStorage.getItem(key)||'null');}catch(_){return null;} }
  function checkedSignoutIntent(value) {
    if(value===null||value===undefined)return null;
    if(!value||typeof value!=='object'||Array.isArray(value)||value.action!=='signout'||value.clientId!==config.clientId)throw new Error('The sign-out request could not be verified. Please choose Sign out again.');
    if(!Number.isFinite(value.at)||value.at>Date.now()||Date.now()-value.at>=SIGNOUT_INTENT_TTL)throw new Error('The sign-out request expired. Please choose Sign out again.');
    return {action:'signout',clientId:config.clientId,at:value.at};
  }
  function clearSignoutIntent(){signoutIntent=null;store(SIGNOUT_INTENT,null);}
  function takeSignoutIntent(){
    const value=stored(SIGNOUT_INTENT),removed=store(SIGNOUT_INTENT,null);
    if(value&&!removed)throw new Error('The sign-out request could not be cleared from browser storage. Open your account to sign out.');
    return checkedSignoutIntent(value);
  }
  function callbackUrl() {
    const u=new URL(config.callbackUrl || config.accountUrl,location.origin);
    if(u.origin!==location.origin||u.protocol!=='https:') throw new Error('The account callback must use this storefront’s HTTPS origin.');
    u.hash=''; ['sign_in','code','state','error','error_description','iss','page','yu_order','yu_order_name','yu_tab','yu_return'].forEach(k=>u.searchParams.delete(k));
    if(!config.callbackUrl) {const p=new URL(location.href).searchParams.get('preview_theme_id') || stored('yu-member-preview-id-v1')?.id; if(p&&/^\d{1,20}$/.test(p)) u.searchParams.set('preview_theme_id',p);}
    return u.href;
  }
  try {space.querySelectorAll('[data-yu-callback-display]').forEach(e=>e.textContent=callbackUrl());}catch(_){space.querySelectorAll('[data-yu-callback-display]').forEach(e=>e.textContent='Open the HTTPS storefront preview, not a local file.');}
  space.querySelectorAll('[data-yu-origin-display]').forEach(e=>e.textContent=location.origin);
  function base64(bytes) {return btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/=/g,'').replace(/\+/g,'-').replace(/\//g,'_');}
  function random() {return base64(crypto.getRandomValues(new Uint8Array(32)));}
  function decode64(s) {if(typeof s!=='string'||!/^[A-Za-z0-9_-]+$/.test(s))throw new Error('Invalid sign-in response.'); return Uint8Array.from(atob(s.replace(/-/g,'+').replace(/_/g,'/')+'='.repeat((4-s.length%4)%4)),c=>c.charCodeAt(0));}
  function requestError(stage, error) {
    if (error?.yuAccountStage) return error;
    const message = error?.name === 'AbortError'
      ? 'The request timed out or was cancelled. Start sign-in again.'
      : error instanceof TypeError
        ? 'The browser could not read the response. Check the network/CORS error in Developer Tools.'
        : (error?.message || 'This request could not be completed.');
    const result = new Error(`${stage}: ${message}`);
    result.yuAccountStage = stage;
    if (error?.http) result.http = error.http;
    if (error?.auth) result.auth = true;
    return result;
  }
  async function fetchJSON(url, options={}, stage='Account request') {
    const controller=new AbortController(); inFlight.add(controller);
    const timer=setTimeout(()=>controller.abort(),20000);
    try {
      const r=await fetch(url,{cache:'no-store',credentials:'omit',referrerPolicy:'no-referrer',...options,signal:controller.signal});
      if(!r.ok) {
        const e=new Error(r.status===401?'Your secure session has expired. Sign in again.':r.status===429?'Please wait a moment before trying again.':`The account service could not complete this request (HTTP ${r.status}).`);
        e.auth=r.status===401; e.http=r.status; throw e;
      }
      try {return await r.json();}catch(_){throw new Error('The account service returned an unreadable response.');}
    } catch(e) {throw requestError(stage,e);}
    finally {clearTimeout(timer);inFlight.delete(controller);}
  }
  async function discover() {
    if(discoveryPromise)return discoveryPromise;
    discoveryPromise=(async()=>{
      const [a,b]=await Promise.all([
        fetchJSON('/.well-known/openid-configuration',{},'Sign-in endpoint discovery'),
        fetchJSON('/.well-known/customer-account-api',{},'Account API discovery')
      ]);
      // Trust comes from the HTTPS shop discovery response, never JWT header URLs
      // or an issuer passed in a callback. Do not construct an alternative host.
      ['authorization_endpoint','token_endpoint','issuer','end_session_endpoint'].forEach(k=>secureUrl(a[k]));
      let endpoint=secureUrl(b.graphql_api);
      if(!/\/customer\/api\/\d{4}-\d{2}\/graphql\/?$/.test(endpoint.pathname))throw new Error('The customer-account endpoint was not recognised.');
      endpoint.pathname=endpoint.pathname.replace(/\/api\/\d{4}-\d{2}\//,`/api/${config.apiVersion}/`);
      auth=Object.freeze({...a}); api=endpoint.href; return auth;
    })().catch(e=>{discoveryPromise=null;throw e;}); return discoveryPromise;
  }

  // Only a fresh JSON object from the direct, discovered HTTPS token endpoint
  // can be checked here. This validates the login transaction binding, not the
  // customer identity. Identity must subsequently be read from Shopify's API.
  // Objects from callbacks, storage, postMessage or an unrelated fetch are refused.
  const directTokenResponses = new WeakMap();
  async function checkDirectLoginBinding(data, nonce, {refresh=false, code=null}={}) {
    const origin = directTokenResponses.get(data);
    if(!origin || origin.endpoint!==auth?.token_endpoint || origin.issuer!==auth?.issuer ||
       origin.client!==config.clientId || origin.epoch!==generation ||
       Date.now()<origin.at || Date.now()-origin.at>30000) {
      throw new Error('A fresh, direct HTTPS sign-in response is required. Please sign in again.');
    }
    directTokenResponses.delete(data); // A transport response is validated once.
    const parts=String(data.id_token||'').split('.');
    if(parts.length!==3 || parts.some(p=>!p) || parts.join('.').length>65536)throw new Error('The sign-in response could not be verified.');
    let header,payload;
    try {
      const decoder=new TextDecoder('utf-8',{fatal:true});
      header=JSON.parse(decoder.decode(decode64(parts[0])));
      payload=JSON.parse(decoder.decode(decode64(parts[1])));
      decode64(parts[2]);
    }catch(_){throw new Error('The sign-in response could not be read. Please sign in again.');}
    if(!header || typeof header!=='object' || Array.isArray(header) ||
       !payload || typeof payload!=='object' || Array.isArray(payload) ||
       !['RS256','ES256'].includes(header.alg) || typeof header.kid!=='string' || !header.kid ||
       header.crit !== undefined || header.b64 === false) {
      throw new Error('Unsupported sign-in token format.');
    }
    if(Array.isArray(auth.id_token_signing_alg_values_supported) &&
       !auth.id_token_signing_alg_values_supported.includes(header.alg))throw new Error('Unexpected sign-in token algorithm.');
    const aud=Array.isArray(payload.aud)?payload.aud:[payload.aud],now=Date.now()/1000;
    // These fixed diagnostics never print any token or claim values.
    // ID-token `sub` is deliberately not the account principal. This is Shopify's
    // OAuth + authenticated-resource flow, with a mandatory API identity step.
    const failures=[];
    if(payload.iss!==auth.issuer)failures.push('ISSUER');
    if(!aud.length || !aud.includes(config.clientId))failures.push('AUDIENCE_CLIENT');
    if(aud.some(a=>a!==config.clientId))failures.push('AUDIENCE_UNTRUSTED');
    if(!Number.isFinite(payload.exp))failures.push('EXPIRY_FORMAT');
    else if(payload.exp<now-30)failures.push('TOKEN_EXPIRED');
    if(!Number.isFinite(payload.iat) || payload.iat<=0)failures.push('ISSUED_AT_FORMAT');
    else if(payload.iat>now+60)failures.push('ISSUED_IN_FUTURE');
    if(Number.isFinite(payload.exp) && Number.isFinite(payload.iat) && payload.exp<=payload.iat)failures.push('TOKEN_TIME_ORDER');
    if(payload.nbf!==undefined) {
      if(!Number.isFinite(payload.nbf))failures.push('NOT_BEFORE_FORMAT');
      else if(payload.nbf>now+60)failures.push('NOT_VALID_YET');
    }
    if(typeof nonce!=='string' || !nonce)failures.push('SAVED_NONCE');
    if(!(refresh&&payload.nonce===undefined) && payload.nonce!==nonce)failures.push(payload.nonce===undefined?'NONCE_MISSING':'NONCE_MISMATCH');
    if(payload.azp!==undefined && payload.azp!==config.clientId)failures.push('AUTHORIZED_PARTY');
    if(aud.length>1 && payload.azp!==config.clientId)failures.push('MULTI_AUDIENCE_PARTY');
    if(failures.length) {
      const clockHint=failures.some(f=>['TOKEN_EXPIRED','ISSUED_IN_FUTURE','NOT_VALID_YET'].includes(f))
        ? ' Check this device’s date and time; the time checks have not been bypassed.' : '';
      throw new Error('Sign-in validation stopped [YU-AUTH: '+failures.join(', ')+
        ']. No new account session was opened. Keep this draft unpublished.'+clockHint);
    }
    // Additional token/code binding, when those optional claims are supplied.
    // Both accepted signing algorithms use SHA-256 for these half-hash claims.
    for(const [claim,value] of [['at_hash',data.access_token],['c_hash',code]]) {
      if(payload[claim]===undefined || (claim==='c_hash'&&refresh))continue;
      if(typeof payload[claim]!=='string'||typeof value!=='string'||!value)throw new Error('The sign-in token binding could not be verified.');
      const digest=new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(value)));
      if(payload[claim]!==base64(digest.slice(0,16)))throw new Error('The sign-in token binding could not be verified.');
    }
    if(origin.epoch!==generation)throw new Error('The account session changed.');
    return true; // Transaction binding only. A customer has NOT been identified.
  }
  async function requestTokens(params, validation) {
    const refresh=params.get('grant_type')==='refresh_token';
    const stage=refresh?'Session refresh':'Sign-in token exchange';
    const endpoint=secureUrl(auth.token_endpoint);
    if(!['authorization_code','refresh_token'].includes(params.get('grant_type')) ||
       params.get('client_id')!==config.clientId)throw new Error('Invalid account authorisation request.');
    const controller=new AbortController(),epoch=generation;inFlight.add(controller);
    const timer=setTimeout(()=>controller.abort(),20000);
    try {
      // TLS authenticity is provided by the browser for this exact discovered
      // HTTPS endpoint. Refuse redirects and opaque/unreadable responses.
      // Never set mode:'no-cors', install a CORS bypass, or use a public proxy.
      const response=await fetch(endpoint.href,{
        method:'POST',mode:'cors',credentials:'omit',redirect:'error',cache:'no-store',
        referrerPolicy:'no-referrer',signal:controller.signal,
        headers:{'Content-Type':'application/x-www-form-urlencoded','Accept':'application/json'},
        body:params
      });
      if(response.redirected || response.url!==endpoint.href || !['basic','cors'].includes(response.type)) {
        throw new Error('The token endpoint did not return a direct HTTPS response.');
      }
      if(!response.ok) {
        const e=new Error(response.status===401?'The client or session was not accepted. Check the public client ID and registered origin.':
          response.status===400?'The authorisation code or request was not accepted. Start a new sign-in attempt.':
          response.status===429?'Please wait a moment before trying again.':`The account service returned HTTP ${response.status}.`);
        e.http=response.status;e.auth=response.status===401;throw e;
      }
      if(!/^application\/json(?:\s*;|\s*$)/i.test(response.headers.get('content-type')||''))throw new Error('The token endpoint did not return JSON.');
      let data;try{data=await response.json();}catch(_){throw new Error('The token response could not be read.');}
      if(epoch!==generation)throw new Error('The account session changed.');
      if(!data || typeof data!=='object' || Array.isArray(data) || typeof data.access_token!=='string' || !data.access_token ||
         !Number.isFinite(Number(data.expires_in)) || Number(data.expires_in)<=0 ||
         (data.token_type!==undefined && (typeof data.token_type!=='string'||data.token_type.toLowerCase()!=='bearer')) ||
         (data.refresh_token!==undefined && (typeof data.refresh_token!=='string'||!data.refresh_token))) {
        throw new Error('The secure account session could not be opened.');
      }
      Object.freeze(data);
      directTokenResponses.set(data,{endpoint:endpoint.href,issuer:auth.issuer,client:config.clientId,at:Date.now(),epoch});
      if(!refresh || data.id_token!==undefined)await checkDirectLoginBinding(data,validation.nonce,{...validation,refresh});
      else directTokenResponses.delete(data); // ID token is optional on refresh.
      if(epoch!==generation)throw new Error('The account session changed.');
      return {data}; // Candidate credentials only; caller MUST resolve API identity.
    }catch(e){throw requestError(stage,e);}
    finally{clearTimeout(timer);inFlight.delete(controller);}
  }
  const identityQuery='query YuSessionCustomer { customer { id } }';
  function identityError(code) {
    // Callers supply fixed diagnostic names only; never customer or token values.
    const e=new Error('Customer verification stopped [YU-AUTH: '+code+']. Sign in again. Keep this draft unpublished.');
    e.auth=true; return e;
  }
  function checkedApiCustomerId(profile,expectedId=null) {
    const id=profile?.id;
    // GraphQL IDs are opaque strings; do not convert token claims/numbers into IDs.
    if(!profile || typeof profile!=='object' || Array.isArray(profile) ||
       typeof id!=='string' || !id || id.length>1024 || /[\s\u0000-\u001f\u007f]/u.test(id)) {
      throw identityError('API_CUSTOMER_ID_MISSING');
    }
    if(expectedId!==null && id!==expectedId)throw identityError('API_CUSTOMER_CHANGED');
    return id;
  }
  async function resolveApiCustomerId(access,expectedId=null,epoch=generation) {
    // This query has no customer-ID argument, email lookup or admin credential.
    // Shopify chooses the customer using the candidate access token alone.
    if(typeof access!=='string' || !access)throw identityError('API_ACCESS_TOKEN_MISSING');
    try {
      const endpoint=secureUrl(api);
      const response=await fetchJSON(endpoint.href,{
        method:'POST',mode:'cors',redirect:'error',
        headers:{'Content-Type':'application/json','Accept':'application/json','Authorization':access},
        body:JSON.stringify({query:identityQuery,variables:{}})
      },'Customer identity check');
      if(epoch!==generation)throw identityError('SESSION_CHANGED');
      if(!response || typeof response!=='object' || Array.isArray(response))throw identityError('API_IDENTITY_RESPONSE');
      if(response.errors!==undefined && (!Array.isArray(response.errors)||response.errors.length)) {
        const codes=Array.isArray(response.errors)?response.errors.map(e=>e?.extensions?.code):[];
        if(codes.includes('THROTTLED'))throw identityError('API_IDENTITY_THROTTLED');
        if(codes.some(c=>['ACCESS_DENIED','UNAUTHORIZED','UNAUTHENTICATED'].includes(c)))throw identityError('API_IDENTITY_ACCESS_DENIED');
        throw identityError('API_IDENTITY_QUERY_REJECTED');
      }
      return checkedApiCustomerId(response.data?.customer,expectedId);
    } catch(e) {throw requestError('Customer identity check',e);}
  }
  // ----- 02.8: account destination selectors (navigation, never authority). -----
  const DESTINATION_KEYS = ['yu_order', 'yu_order_name', 'yu_tab', 'yu_return', 'yu_return_view'];
  let destination = null, destinationInvalid = false, destinationSerial = 0;
  let selectingDestination = false;
  function plainReference(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 160 &&
      value === value.trim() && !/[\u0000-\u001f\u007f<>]/.test(value) ? value : null;
  }
  function numericOrderId(value) {
    if (typeof value !== 'string') return null;
    const m = /^(?:gid:\/\/shopify\/Order\/)?([1-9]\d{0,19})$/.exec(value);
    return m ? m[1] : null;
  }
  function validDestination(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value) ||
        !['orders','returns'].includes(value.tab) || !['id','name'].includes(value.kind)) return null;
    const ref = value.kind === 'id' ? numericOrderId(value.ref) : plainReference(value.ref);
    const returnRef = value.returnRef ? plainReference(value.returnRef) : '';
    if (!ref || (value.returnRef && !returnRef) || (returnRef && value.tab !== 'returns') || (value.viewReturnOnly && value.tab !== 'returns')) return null;
    const result={kind:value.kind, ref, tab:value.tab, returnRef};
    if(value.viewReturnOnly===true)result.viewReturnOnly=true;
    return Object.freeze(result);
  }
  function readDestination(url = new URL(location.href)) {
    const p=url.searchParams;
    const present=DESTINATION_KEYS.some(k=>p.has(k));
    if (!present) return {value:null,invalid:false};
    if (DESTINATION_KEYS.some(k=>p.getAll(k).length>1)) return {value:null,invalid:true};
    const hasId=p.has('yu_order'),hasName=p.has('yu_order_name');
    const tab=p.has('yu_tab')?p.get('yu_tab'):'orders';
    if (!['orders','returns'].includes(tab)) return {value:null,invalid:true};
    const hasReturnView=p.has('yu_return_view');
    if(hasReturnView && (p.get('yu_return_view')!=='1' || tab!=='returns' || (!hasId && !hasName)))return {value:null,invalid:true};
    if (!hasId&&!hasName) {
      // General account tabs select a panel only; they never identify a record.
      if (!p.has('yu_tab')||p.has('yu_return')) return {value:null,invalid:true};
      return {value:null,invalid:false,tab};
    }
    if (hasId&&hasName) return {value:null,invalid:true};
    const value=validDestination({kind:hasId?'id':'name',ref:p.get(hasId?'yu_order':'yu_order_name'),
      tab,returnRef:p.get('yu_return') || '',viewReturnOnly:hasReturnView});
    return {value,invalid:!value};
  }
  function selectGeneralAccountTab(tab) {
    if(!['orders','returns'].includes(tab))return;
    const u=new URL(location.href);u.hash=tab;
    history.replaceState(history.state,'',u.pathname+u.search+u.hash);
    selectingDestination=true;
    try {space.selectTab(tab,false,true);} finally {selectingDestination=false;}
  }
  function writeDestinationUrl(value) {
    const u=new URL(location.href);
    DESTINATION_KEYS.forEach(k=>u.searchParams.delete(k));
    if(value) {
      u.searchParams.set(value.kind==='id'?'yu_order':'yu_order_name',value.ref);
      u.searchParams.set('yu_tab',value.tab);
      if(value.returnRef)u.searchParams.set('yu_return',value.returnRef);
      if(value.viewReturnOnly)u.searchParams.set('yu_return_view','1');
      u.hash=value.tab;
    }
    history.replaceState(history.state,'',u.pathname+u.search+u.hash);
  }
  function adoptDestination(value) {
    destination=validDestination(value);destinationInvalid=false;destinationSerial++;
    writeDestinationUrl(destination);
    if(destination) {
      selectingDestination=true;
      try {space.selectTab(destination.tab,false,true);} finally {selectingDestination=false;}
    }
  }
  function clearDestination() {adoptDestination(null);}
  function linkedAccountUrl(orderId, tab='orders', returnRef='') {
    const id=numericOrderId(orderId);
    if(!id || !['orders','returns'].includes(tab)) return null;
    const u=new URL(callbackUrl());
    DESTINATION_KEYS.forEach(k=>u.searchParams.delete(k));
    u.searchParams.set('yu_order',id);u.searchParams.set('yu_tab',tab);
    if(returnRef && plainReference(returnRef))u.searchParams.set('yu_return',returnRef);
    u.hash=tab;
    return u.href;
  }
  function addDestinationTestLinks(root, orderId, returnRef='') {
    if(!config.previewNotice)return;
    const box=node('div',null,'yu-member-fineprint');box.dataset.yuLinkTest='';
    const choices=returnRef ? [['Test this return link','returns',returnRef]] :
      [['Test order link','orders',''],['Test returns link','returns','']];
    for(const [label,tab,ref] of choices) {
      const href=linkedAccountUrl(orderId,tab,ref);if(!href)continue;
      if(box.childNodes.length)box.append(document.createTextNode(' · '));
      const a=node('a',label,'yu-member-text-link');a.href=href;a.target='_blank';a.rel='noopener noreferrer nofollow';box.append(a);
    }
    if(box.childNodes.length)root.append(box);
  }
  function sameLinkedOrder(order, link) {
    return !!order && (link.kind==='id' ? numericOrderId(order.id)===link.ref : order.name===link.ref);
  }
  function destinationCurrent(epoch,customerId,serial) {
    return epoch===generation && session?.customerId===customerId && serial===destinationSerial;
  }
  function focusLinkedElement(el) {
    if(!el?.isConnected)return;
    el.tabIndex=-1;el.focus({preventScroll:true});
    // Let existing account CSS/fixed-header clearance determine the offset.
    el.style.scrollMarginTop='calc(var(--header-height, 100px) + 24px)';
    el.scrollIntoView({block:'start',behavior:'auto'});
  }
  async function openAccountDestination(options={}) {
    if(destinationInvalid) {
      status('This account link could not be recognised. Open Orders & tracking or contact YŪ SEI.',true);return;
    }
    const link=destination;
    if(!link || !session || !customer)return;
    const epoch=generation,expectedId=session.customerId,serial=++destinationSerial;
    try {
      // Quoted values escape the Shopify search grammar; all results also need
      // an exact ID/name match. Never trust the first result of a loose search.
      const query=link.kind==='id' ? `id:${link.ref}` : `name:${JSON.stringify(link.ref)}`;
      const data=await gql(Q.linkedOrder,{query});
      if(!destinationCurrent(epoch,expectedId,serial))return;
      checkedApiCustomerId(data.customer,expectedId);
      const conn=data.customer.orders;
      if(!conn || !Array.isArray(conn.nodes))throw new Error('The linked order could not be checked.');
      const matches=conn.nodes.filter(o=>sameLinkedOrder(o,link));
      if(matches.length!==1 || conn.pageInfo?.hasNextPage) {
        status('This order is not available in the signed-in account. Use the email used at checkout, or contact YŪ SEI. If the purchase was just made, wait a moment and refresh.',true);return;
      }
      const order=matches[0];
      if(!numericOrderId(order.id))throw new Error('The linked order reference could not be verified.');
      selectingDestination=true;
      try {space.selectTab(link.tab,false,true);} finally {selectingDestination=false;}
      if(link.tab==='returns') {
        await openReturn(order.id,{viewReturnOnly:Boolean(options.viewReturnOnly||link.viewReturnOnly)});
        if(!destinationCurrent(epoch,expectedId,serial))return;
        const root=$('[data-yu-returns-content]');
        if(link.returnRef) {
          const found=[...root.querySelectorAll('[data-yu-return-id]')].find(el=>
            el.dataset.yuReturnId===link.returnRef || el.dataset.yuReturnName===link.returnRef ||
            el.dataset.yuReturnId===`gid://shopify/Return/${link.returnRef}`);
          if(found)focusLinkedElement(found);
          else {status('The order’s return information is open, but this specific return record is not in the loaded records. Contact YŪ SEI if it is missing.',true);focusLinkedElement(root);}
        } else focusLinkedElement(root);
      } else {
        const root=$('[data-yu-orders-content]');
        const back=btn('← All orders',()=>{clearDestination();renderOrders();},'yu-member-text-link yu-detail-back');
        const card=node('article',null,'yu-member-order');card.dataset.yuPrivate='';
        const h=heading(order.name);h.classList.add('yu-detail-legacy-title');card.append(h);
        const body=node('div',null,'yu-account-detail-inner');card.append(body);root.replaceChildren(back,card);
        body.append(paragraph('Loading order details…'));
        await loadDetail(order.id,body);
        if(!destinationCurrent(epoch,expectedId,serial))return;
        addDestinationTestLinks(card,order.id);
        const refundDetails=options.focusRefunds ? body.querySelector('.yu-return-refund-details') : null;
        if(refundDetails)focusLinkedElement(refundDetails);
        else focusLinkedElement(body.querySelector('.yu-detail-page-title')||h);
      }
    } catch(error) {
      if(!destinationCurrent(epoch,expectedId,serial))return;
      status(error.auth ? 'Sign in again to open this order.' :
        'The linked order or return could not be opened. Refresh to retry, or open Orders & tracking. '+errorText(error),true);
    }
  }
  const initialDestination=readDestination();
  destination=initialDestination.value;destinationInvalid=initialDestination.invalid;
  if(destination)adoptDestination(destination);
  else if(initialDestination.tab)selectGeneralAccountTab(initialDestination.tab);
  space.addEventListener('yu:account-tab',event=>{
    if(!selectingDestination && destination && event.detail?.name!==destination.tab)clearDestination();
  });
  // Deliberate sidebar navigation opens the list, even if Orders is already
  // selected. Programmatic tab selections and order links retain their target.
  function showOrdersListFromNavigation(){
    if(destination || destinationInvalid)clearDestination();
    if(session && customer && session.customerId===customer.id)renderOrders();
  }
  space.addEventListener('click',event=>{
    const target=event.target instanceof Element ? event.target : null;
    const ordersTab=target?.closest('[data-yu-member-tab="orders"]');
    if(!ordersTab || !space.contains(ordersTab) || event.button!==0 ||
       event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)return;
    showOrdersListFromNavigation();
  });
  space.addEventListener('keydown',event=>{
    const target=event.target instanceof Element ? event.target : null;
    const tab=target?.closest('[data-yu-member-tab]');
    if(!tab || !space.contains(tab) || !event.defaultPrevented ||
       !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key))return;
    if(space.querySelector('[data-yu-member-tab="orders"][aria-selected="true"]'))showOrdersListFromNavigation();
  });
  // ----- End 02.8 destination router. -----

  // A new tab intentionally has neither the first tab's API token nor its
  // sessionStorage hints. Ask Shopify, not the hint, whether sign-in is active.
  function contactOutcomePresent() {
    return Boolean(space.querySelector('[data-yu-support-success], [data-yu-support-errors], #YuAccountHelpForm .errors'));
  }
  function connectionPending() {
    const text='Checking your existing Shopify sign-in…';
    const el=$('[data-yu-session]');
    if(el){el.removeAttribute('data-yu-private');el.replaceChildren(paragraph('Checking your sign-in…'));}
    ['[data-yu-orders-content]','[data-yu-profile-content]','[data-yu-returns-content]'].forEach(selector=>{
      const root=$(selector);if(root)root.replaceChildren(paragraph(text));
    });
    status(text);
  }
  async function checkExistingSignIn() {
    if(pageLeaving||session||authenticating||!config.enabled||!config.clientId||config.designMode)return;
    connectionPending();
    await beginLogin(true);
    if(!authenticating&&!pageLeaving&&!session){clearPrivate();renderSession(false);}
  }
  function resumeAfterPeerLogin() {
    if(signoutIntent||!peerReconnectPending||!startupSettled||pageLeaving||document.hidden||session||authenticating)return;
    if(!config.enabled||!config.clientId||config.designMode||contactOutcomePresent())return;
    // Never navigate away from a customer composing a support enquiry.
    const selected=space.querySelector('[data-yu-member-tab][aria-selected="true"]');
    if(selected?.dataset.yuMemberTab==='help')return;
    peerReconnectPending=false;
    void checkExistingSignIn().catch(error=>status(errorText(error),true));
  }
  window.addEventListener('focus',resumeAfterPeerLogin);
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)resumeAfterPeerLogin();});
  space.addEventListener('yu:account-tab',()=>queueMicrotask(resumeAfterPeerLogin));

  async function beginLogin(silent=false) {
    if(!silent)clearSignoutIntent();
    if(authenticating)return; if(config.designMode){status('Open the account in a separate preview tab to test sign-in.',true);return;}
    authenticating=true;
    const loginEpoch=generation;
    try {
      await discover();
      if(pageLeaving||loginEpoch!==generation){authenticating=false;return;}
      if(!crypto?.subtle)throw new Error('A secure browser connection is required.');
      const verifier=random(), state=random(), nonce=random(), redirect=callbackUrl();
      const tx={verifier,state,nonce,redirect,at:Date.now(),silent,panel:location.hash.slice(1)||'orders',client:config.clientId,destination,invalidDestination:destinationInvalid};
      if(signoutIntent){if(!silent)throw new Error('Sign-out cannot start an interactive sign-in.');tx.signoutIntent=checkedSignoutIntent(signoutIntent);}
      if(!store(TX,tx))throw new Error('Allow this site to use session storage, then try signing in again.');
      const challenge=base64(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(verifier)));
      if(pageLeaving||loginEpoch!==generation){store(TX,null);authenticating=false;return;}
      const u=new URL(auth.authorization_endpoint);
      Object.entries({client_id:config.clientId,response_type:'code',scope:'openid email customer-account-api:full',redirect_uri:redirect,state,nonce,code_challenge:challenge,code_challenge_method:'S256'}).forEach(([k,v])=>u.searchParams.set(k,v));
      if(silent)u.searchParams.set('prompt','none');
      if(config.locale)u.searchParams.set('locale',config.locale);
      if(config.country)u.searchParams.set('region_country',config.country);
      location.assign(u.href);
    }catch(e){const signingOut=Boolean(signoutIntent);clearSignoutIntent();store(TX,null);authenticating=false;status(signingOut?'Sign-out could not be completed. '+(e.message||'Please try again.'):e.message||'Sign-in is temporarily unavailable.',true);}
  }
  async function exchange(callback) {
    const tx=stored(TX);store(TX,null);
    if(!tx||!Number.isFinite(tx.at)||typeof tx.verifier!=='string'||!/^[A-Za-z0-9_-]{43,128}$/.test(tx.verifier)||typeof tx.nonce!=='string'||!tx.nonce||tx.client!==config.clientId||!callback.state||callback.state!==tx.state||Date.now()-tx.at>600000||Date.now()<tx.at||tx.redirect!==callbackUrl())throw new Error('This sign-in attempt has expired or cannot be verified. Please sign in again.');
    signoutIntent=checkedSignoutIntent(tx.signoutIntent);
    if(signoutIntent&&!tx.silent)throw new Error('The sign-out request was not a silent session check. Please choose Sign out again.');
    await discover();if(callback.issuer&&callback.issuer!==auth.issuer)throw new Error('The sign-in issuer could not be verified.');
    // Restore navigation only after the existing transaction/state checks.
    if(tx.destination)adoptDestination(tx.destination); else if(tx.invalidDestination===true)destinationInvalid=true;
    else selectGeneralAccountTab(tx.panel); // Also retain a general tab after a silent login_required response.
    if(callback.error){store(RESUME,null);if(tx.silent&&['login_required','interaction_required','consent_required'].includes(callback.error)){status('Sign in to securely access your account tools.');return false;}throw new Error('Sign-in was not completed. Please try again.');}
    if(!callback.code)throw new Error('No sign-in code was received.');
    const epoch=generation;
    const {data}=await requestTokens(new URLSearchParams({grant_type:'authorization_code',client_id:config.clientId,code:callback.code,redirect_uri:tx.redirect,code_verifier:tx.verifier}),{nonce:tx.nonce,code:callback.code});
    const expires=Date.now()+Number(data.expires_in)*1000;
    // A token response is not sufficient: no session is committed before this read.
    const customerId=await resolveApiCustomerId(data.access_token,null,epoch);
    if(epoch!==generation)throw identityError('SESSION_CHANGED');
    if(expires<=Date.now())throw identityError('ACCESS_TOKEN_EXPIRED');
    session={access:data.access_token,refresh:data.refresh_token||null,id:data.id_token,nonce:tx.nonce,customerId,expires};
    if(signoutIntent)checkedSignoutIntent(signoutIntent);
    else {store(RESUME,{at:Date.now(),client:config.clientId});space.selectTab(tx.panel,false,true);}
    return true;
  }
  async function validToken() {
    if(!session || typeof session.customerId!=='string' || !session.customerId) {const e=new Error('Sign in to securely access your account tools.');e.auth=true;throw e;}
    if(session.expires>Date.now()+45000)return session.access;
    if(!session.refresh){const e=new Error('Your secure session has expired. Sign in again.');e.auth=true;throw e;}
    if(!refreshPromise) refreshPromise=(async()=>{
      const old=session,epoch=generation;
      const {data}=await requestTokens(new URLSearchParams({grant_type:'refresh_token',client_id:config.clientId,refresh_token:old.refresh}),{nonce:old.nonce});
      const expires=Date.now()+Number(data.expires_in)*1000;
      if(!session||session!==old||epoch!==generation)throw identityError('SESSION_CHANGED');
      // Even if no ID token is returned on refresh, the customer must stay the same.
      await resolveApiCustomerId(data.access_token,old.customerId,epoch);
      if(!session||session!==old||epoch!==generation)throw identityError('SESSION_CHANGED');
      if(expires<=Date.now())throw identityError('ACCESS_TOKEN_EXPIRED');
      session={...old,access:data.access_token,refresh:data.refresh_token||old.refresh,id:data.id_token||old.id,expires};return session.access;
    })().catch(e=>{e.auth=true;throw e;}).finally(()=>refreshPromise=null);
    return refreshPromise;
  }
  async function gql(query,variables={},write=false) {
    if(write&&(!config.allowWrites||config.designMode))throw new Error('Changes are locked for read-only testing.');
    const epoch=generation;
    try {
      const token=await validToken();
      const data=await fetchJSON(api,{method:'POST',mode:'cors',redirect:'error',headers:{'Content-Type':'application/json','Authorization':token},body:JSON.stringify({query,variables})},'Customer account data');
      if(epoch!==generation)throw new Error('The account session changed.');
      if(data.errors?.length){const e=new Error(data.errors.some(x=>x.extensions?.code==='THROTTLED')?'Please wait a moment, then try again.':data.errors.some(x=>['UNAUTHENTICATED','UNAUTHORIZED'].includes(x.extensions?.code))?'Your secure session has expired. Sign in again.':'This account action is not available. Please contact YŪ SEI.');e.auth=data.errors.some(x=>['UNAUTHENTICATED','UNAUTHORIZED'].includes(x.extensions?.code));e.api=true;e.uncertain=write&&!e.auth&&!data.errors.every(x=>x.extensions?.code==='THROTTLED');throw e;}
      if(!data.data)throw new Error('No account information was returned.');return data.data;
    }catch(e){if(e.auth)expire();if(write&&!e.api&&!e.auth&&(!e.http||e.http>=500))e.uncertain=true;throw e;}
  }
  async function mutate(query,variables,key) {
    const data=await gql(query,variables,true), result=data[key];
    if(!result) {const e=new Error('The result could not be confirmed. Refresh your account before trying again.');e.uncertain=true;throw e;}
    if(result.userErrors?.length)throw new Error(result.userErrors.map(e=>e.message).join(' '));return result;
  }
  function clearPrivate() {
    closeAddressModal(false, true);
    marketingWrite=null;marketingNeedsRefresh=false;marketingNotice='';marketingRevision++;nameSaveNotice='';
    destinationSerial++;returnGeneration++;ordersLoadPromise=null;addressCreateNeedsRefresh=false;
    generation++;inFlight.forEach(c=>c.abort());inFlight.clear();session=null;customer=null;orders=[];pageInfo=null;renderGreeting(false);
    ['[data-yu-orders-content]','[data-yu-profile-content]','[data-yu-returns-content]'].forEach(s=>{const el=$(s);if(el)el.replaceChildren(paragraph('Sign in to view your account.'),btn('Sign in',()=>beginLogin(false),'yu-member-button yu-member-button--compact'));});
    const returnContent=$('[data-yu-returns-content]');
    if(returnContent){
      delete returnContent.dataset.yuReturnOverviewResult;
      delete returnContent.dataset.yuReturnView;
      const empty=node('div',null,'yu-member-empty yu-returns-signed-out');
      empty.append(heading('Your returns, made simple.',3),paragraph('Sign in with the email you used at checkout.'),btn('Sign in to view returns ↗',()=>beginLogin(false),'yu-member-button yu-member-button--compact'));
      returnContent.replaceChildren(empty);
    }
    space.querySelectorAll('[data-yu-private]').forEach(el=>{
      // Retain the session container so signed-out/re-login controls can render.
      if(el.matches('[data-yu-session]')) {el.replaceChildren();el.removeAttribute('data-yu-private');}
      else if(!el.closest('[data-yu-orders-content], [data-yu-profile-content], [data-yu-returns-content]'))el.remove();
    });
    $('#YuAccountHelpForm')?.reset();
    publishAccountPresentation('signed-out');
  }
  function expire(){store(RESUME,null);clearPrivate();renderSession(false);status('Your secure session has expired. Sign in again to continue.',true);}
  async function signout(){
    clearSignoutIntent();
    lastExplicitLogoutAt=Date.now();peerReconnectPending=false;
    const id=session?.id;clearPrivate();store(RESUME,null);store(TX,null);logoutChannel?.postMessage('logout');
    if(id&&auth){store(LOGOUT,{at:Date.now()});const u=new URL(auth.end_session_endpoint);u.searchParams.set('id_token_hint',id);u.searchParams.set('post_logout_redirect_uri',callbackUrl());location.assign(u.href);}
    else location.assign(config.logoutUrl||'/account/logout');
  }
  logoutChannel?.addEventListener('message',e=>{
    if(e.data==='logout'){
      clearSignoutIntent();
      lastExplicitLogoutAt=Date.now();peerReconnectPending=false;
      store(RESUME,null);store(TX,null);clearPrivate();renderSession(false);status('You have signed out.');return;
    }
    const hint=e.data;
    if(!hint||hint.type!=='yu-account-ui-recent-024'||hint.clientId!==config.clientId||
       !Number.isFinite(hint.at)||hint.at>Date.now()||Date.now()-hint.at>=86400000||
       hint.at<=lastPeerNoticeAt||hint.at<=lastExplicitLogoutAt)return;
    lastPeerNoticeAt=hint.at;
    if(signoutIntent||session||pageLeaving)return;
    // This message authorises no data access. It only prompts Shopify to check
    // its own browser session using the unchanged PKCE/customer-validation flow.
    peerReconnectPending=true;resumeAfterPeerLogin();
  });
  function renderGreeting(verified) {
    const title=space.querySelector('.yu-orders-sidebar-title');
    if(!title)return;
    if(!verified || !session || !customer || customer.id!==session.customerId){title.textContent='A quieter space.';return;}
    const hour=new Date().getHours(), greeting=hour>=5&&hour<12?'Good morning':hour>=12&&hour<17?'Good afternoon':'Good evening';
    const name=typeof customer.firstName==='string'?customer.firstName.trim():'';
    title.textContent=name?`${greeting}, ${name}.`:`${greeting}.`;
  }
  function refreshGreeting(){if(!pageLeaving && session && customer && customer.id===session.customerId)renderGreeting(true);}
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)refreshGreeting();});
  setInterval(()=>{if(!document.hidden)refreshGreeting();},60000);
  function renderSession(signedIn=true) {
    const verified = Boolean(signedIn && customer && session && customer.id === session.customerId);
    renderGreeting(verified);
    const el=$('[data-yu-session]');
    if(el) {
      if(verified) {el.replaceChildren(paragraph(customer.firstName||'Your account','yu-classic-customer-name'),btn('Sign out',signout));el.dataset.yuPrivate='';}
      else {el.removeAttribute('data-yu-private');el.replaceChildren(btn('Sign in or create account',()=>beginLogin(false)));}
    }
    publishAccountPresentation(verified ? 'verified' : 'signed-out');
  }
  // A delegated handler also covers the sign-out link added to the drawer after
  // API login. No once-only handlers are left attached to old UI elements.
  document.addEventListener('click', e => {
    const link=e.target instanceof Element?e.target.closest('[data-yu-member-signout]'):null;
    if(!link || e.defaultPrevented || e.button!==0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || !session)return;
    e.preventDefault();
    const dialog=link.closest('dialog');if(dialog?.open)dialog.close();
    signout().catch(error=>status(errorText(error),true));
  });
  function errorText(e) {return e.uncertain?'We could not confirm whether this change was saved. Refresh your account before trying again; do not submit it twice.':e.message||'This action could not be completed. Please try again.';}
  async function withButton(button,task,message) {
    if(button.disabled)return;button.disabled=true;button.setAttribute('aria-busy','true');
    let uncertain=false;
    try {await task();}catch(e){uncertain=Boolean(e.uncertain);if(message){message.textContent=errorText(e);message.focus?.();}else status(errorText(e),true);}finally{button.disabled=uncertain;button.removeAttribute('aria-busy');}
  }
  function formMessage(){const p=paragraph('','yu-account-form-message');p.setAttribute('role','status');p.tabIndex=-1;return p;}
  function field(form,name,label,value='',opts={}) {
    const id=`YuField${++serial}`, wrap=node('label',label);wrap.htmlFor=id;
    let input;if(opts.choices){input=node('select');for(const [v,t] of opts.choices){const o=node('option',t);o.value=v;input.append(o);}}else input=node(opts.textarea?'textarea':'input');
    input.id=id;input.name=name;input.setAttribute('aria-label',label); if(!opts.choices&&!opts.textarea)input.type=opts.type||'text';if(opts.required)input.required=true;
    if(opts.max)input.maxLength=opts.max;if(opts.autocomplete)input.autocomplete=opts.autocomplete;
    input.value=value??'';wrap.append(input);form.append(wrap);return input;
  }
  function writeNotice(target){if(!config.allowWrites)target.append(paragraph('Read-only test: profile saves and return submissions are currently locked.','yu-account-notice'));}
  async function loadCustomer() {
    const epoch=generation,expectedId=session?.customerId,preferenceRevision=marketingRevision;
    if(!expectedId)throw identityError('API_CUSTOMER_NOT_VERIFIED');
    const data=await gql(Q.customer);
    if(epoch!==generation || session?.customerId!==expectedId)throw identityError('SESSION_CHANGED');
    let profile=data.customer;
    try {checkedApiCustomerId(profile,expectedId);}catch(e){expire();throw e;}
    if(!profile.addresses || !Array.isArray(profile.addresses.nodes))throw new Error('Your customer addresses could not be loaded.');
    let page=profile.addresses.pageInfo;
    while(page?.hasNextPage){
      const extra=await gql(Q.addresses,{after:page.endCursor});
      if(epoch!==generation || session?.customerId!==expectedId)throw identityError('SESSION_CHANGED');
      if(!Array.isArray(extra.customer?.addresses?.nodes))throw new Error('Your customer addresses could not be loaded.');
      profile.addresses.nodes.push(...extra.customer.addresses.nodes);page=extra.customer.addresses.pageInfo;
    }
    if(epoch!==generation || session?.customerId!==expectedId)throw identityError('SESSION_CHANGED');
    // Ignore a read started before a preference write settled; its state may be stale.
    if(preferenceRevision!==marketingRevision)return;
    if(!marketingWrite&&marketingNeedsRefresh){marketingNeedsRefresh=false;marketingNotice='Your email preference has been refreshed.';}
    customer=profile;renderSession();renderProfile();
    // The API identity supersedes any theme-cookie greeting inside this account.
    document.querySelectorAll('.yu-member-sheet-footer [data-yu-member-signout]').forEach(a=>a.textContent='Sign out');
    const email=$('#YuAccountHelpForm input[type=email]');if(email)email.value=customer.emailAddress?.emailAddress||'';
    const name=$('#YuAccountHelpForm input[name="contact[name]"]');if(name)name.value=[customer.firstName,customer.lastName].filter(Boolean).join(' ');
  }
  async function loadOrders(append=false) {
    const epoch=generation;
    if(ordersLoadPromise){
      if(append||!ordersLoadAppend)return ordersLoadPromise;
      try {await ordersLoadPromise;} catch(e) {if(e.auth||epoch!==generation)throw e;}
      if(epoch!==generation)return;
      return loadOrders(false);
    }
    ordersLoadAppend=append;
    const pending=(async()=>{
      const data=await gql(Q.orders,{after:append?pageInfo?.endCursor:null});
      if(epoch!==generation)return;
      const conn=data.customer?.orders;if(!conn)throw new Error('Your orders could not be loaded.');
      orders=append?[...new Map([...orders,...conn.nodes].map(order=>[order.id,order])).values()]:conn.nodes;
      pageInfo=conn.pageInfo;renderOrders();renderReturnList();
    })();
    ordersLoadPromise=pending;
    try {await pending;} finally {if(ordersLoadPromise===pending)ordersLoadPromise=null;}
  }
  function renderOrders(){
    const root=$('[data-yu-orders-content]');root.replaceChildren();root.dataset.yuPrivate='';
    if(!orders.length){
      const empty=node('div',null,'yu-member-empty yu-orders-empty');
      const shop=node('a',null,'yu-member-button yu-member-button--compact');
      shop.href=space.dataset.yuMatchaUrl||'/products/yu-sei-matcha';
      shop.dataset.yuMemberLink='';
      shop.textContent='SHOP MATCHA';
      empty.append(paragraph('YOUR MATCHA JOURNEY','yu-orders-eyebrow'),heading('Your first bowl awaits.'),paragraph('No orders are showing for this account yet.'),shop);
      root.append(empty);return;
    }
    const list=node('div',null,'yu-member-orders');
    for(const o of orders){const card=node('article',null,'yu-member-order yu-classic-order-card');
      const top=node('div',null,'yu-account-detail-heading yu-classic-order-header');
      const identity=node('div',null,'yu-classic-order-identity');identity.append(heading(`Order ${o.name}`),paragraph(date(o.createdAt),'yu-member-fineprint yu-classic-order-date'));
      const states=node('div',null,'yu-classic-order-statuses');states.append(node('span',o.cancelledAt?'Cancelled':human(o.fulfillmentStatus),'yu-member-status'),node('span',human(o.financialStatus),'yu-member-status'));
      top.append(identity,states);card.append(top);
      const products=node('div',null,'yu-classic-products');
      for(const l of o.lineItems.nodes){
        const row=node('div',null,'yu-classic-product');
        if(l.image?.url){try{const url=new URL(l.image.url);if(url.protocol==='https:'||url.protocol==='http:'){const image=node('img',null,'yu-classic-product-image');image.src=url.href;image.alt=l.image.altText||itemName(l);image.loading='lazy';image.decoding='async';row.append(image);}}catch(_){}}
        const info=node('div',null,'yu-classic-product-info');info.append(heading(itemName(l),4),paragraph(`Quantity: ${l.quantity}`,'yu-member-fineprint'));row.append(info);products.append(row);
      }
      card.append(products,paragraph(money(o.totalPrice),'yu-classic-order-price'));
      const detail=node('details',null,'yu-account-order-detail'),summary=node('summary','View order','yu-member-text-link yu-classic-view-order'),body=node('div',null,'yu-account-detail-inner');
      detail.append(summary,body);let loaded=false,busy=false,deliveryFocusRequested=false;
      const cardEpoch=generation,cardCustomerId=session?.customerId;
      const cardCurrent=()=>Boolean(cardCustomerId&&generation===cardEpoch&&session?.customerId===cardCustomerId&&customer?.id===cardCustomerId&&body.isConnected&&!pageLeaving);
      const focusDelivery=()=>{if(!deliveryFocusRequested||!detail.open||!cardCurrent())return;const target=body.querySelector('.yu-classic-delivery');if(target){deliveryFocusRequested=false;focusLinkedElement(target);}};
      detail.addEventListener('toggle',async()=>{
        summary.textContent=detail.open?'Back to orders':'View order';
        if(!detail.open){deliveryFocusRequested=false;return;}
        if(loaded){focusDelivery();return;}
        if(busy||!cardCurrent())return;
        busy=true;body.replaceChildren(paragraph('Loading order details…'));
        try{await loadDetail(o.id,body);if(!cardCurrent())return;loaded=true;focusDelivery();}
        catch(e){if(!cardCurrent())return;body.replaceChildren(paragraph(errorText(e)),btn('Try again',()=>{loaded=false;detail.open=false;setTimeout(()=>detail.open=true,0);}));}
        finally{busy=false;}
      });
      const shipments=Array.isArray(o.fulfillments?.nodes)?o.fulfillments.nodes:[],trackingUrls=new Map();
      const trackingComplete=Array.isArray(o.fulfillments?.nodes)&&o.fulfillments.pageInfo?.hasNextPage===false&&shipments.every(f=>f&&Array.isArray(f.trackingInformation)&&f.trackingInformation.length>0&&f.trackingInformation.every(t=>t&&Boolean(external('Track order',t.url))));
      for(const f of shipments){for(const t of Array.isArray(f?.trackingInformation)?f.trackingInformation:[]){const link=external('Track order',t?.url);if(link)trackingUrls.set(link.href,link);}}
      let tracking;
      if(!o.cancelledAt&&trackingComplete&&trackingUrls.size===1){tracking=trackingUrls.values().next().value;tracking.className='yu-member-text-link yu-order-track yu-classic-order-track';}
      else{tracking=btn(!o.cancelledAt&&trackingUrls.size?'Track order':'Tracking updates',()=>{if(!cardCurrent())return;deliveryFocusRequested=true;detail.open=true;if(loaded)focusDelivery();},'yu-member-text-link yu-order-track yu-classic-order-track');}
      card.append(tracking,detail);addDestinationTestLinks(card,o.id);list.append(card);
    }root.append(list);
    if(pageInfo?.hasNextPage){const more=btn('Load earlier orders',()=>withButton(more,()=>loadOrders(true)));root.append(more);}
  }
  async function loadDetail(id,root){
    const data=await gql(Q.detail,{id});const order=data.order;if(!order)throw new Error('This order is not available for this account.');
    while(order.lineItems.pageInfo?.hasNextPage){const extra=await gql(Q.moreLines,{id,after:order.lineItems.pageInfo.endCursor});order.lineItems.nodes.push(...extra.order.lineItems.nodes);order.lineItems.pageInfo=extra.order.lineItems.pageInfo;}
    renderDetail(order,root);
    await enhanceOrderTotals(id,root,order.name);
    return order;
  }
  // Quantities belong to this shipment/return, not the order's full line item.
  function renderRecordItems(connection,root,kind,orderName){
    const items=Array.isArray(connection?.nodes)?connection.nodes.filter(Boolean):[];
    let unavailable=!items.length;
    if(items.length){
      root.append(paragraph(`Items in this ${kind}`,'yu-member-fineprint'));
      const list=node('ul',null,'yu-member-fineprint');
      for(const item of items){
        const line=item.lineItem,quantity=item.quantity;
        const knownQuantity=Number.isInteger(quantity)&&quantity>=0;
        if(!line||!knownQuantity)unavailable=true;
        list.append(node('li',`${itemName(line)}${line?.variantTitle&&line.variantTitle!=='Default Title'?' - '+line.variantTitle:''}${knownQuantity?' × '+quantity:' · Quantity unavailable'}`));
      }
      root.append(list);
    }
    if(unavailable||connection?.pageInfo?.hasNextPage!==false){
      const message=connection?.pageInfo?.hasNextPage===true?
        `Some items in this ${kind} are not shown. Contact YŪ SEI for the complete item details.`:
        `The complete item details for this ${kind} could not be confirmed. Contact YŪ SEI for help.`;
      root.append(paragraph(message,'yu-member-fineprint'),helpLink(`Ask about items in this ${kind}`,kind==='return'?'Return enquiry':'Order enquiry',orderName));
    }
  }
  function renderDetail(o,root){
    root.replaceChildren();root.dataset.yuPrivate='';root.classList.add('yu-detail-page');
    const header=node('header',null,'yu-detail-page-header'),title=node('h1',`Order ${o.name}`,'yu-member-title yu-detail-page-title'),meta=node('div',null,'yu-detail-page-meta');
    const placed=date(o.createdAt);meta.append(paragraph(placed?`Placed ${placed}`:'Placed date unavailable','yu-detail-placed'),node('span',human(o.financialStatus),'yu-member-status yu-detail-financial-status'));header.append(title,meta);root.append(header);
    const layout=node('div',null,'yu-classic-order-grid'),main=node('div',null,'yu-classic-order-main'),delivery=node('div',null,'yu-classic-delivery');
    const items=node('div',null,'yu-classic-items yu-classic-card');items.append(heading('Order summary'));
    const lines=Array.isArray(o.lineItems?.nodes)?o.lineItems.nodes.filter(Boolean):[];
    for(const l of lines){
      const row=node('div',null,'yu-account-line yu-detail-product');
      if(l.image?.url){try{const url=new URL(l.image.url);if(url.protocol==='https:'||url.protocol==='http:'){const image=node('img',null,'yu-detail-product-image');image.src=url.href;image.alt=l.image.altText||itemName(l);image.loading='lazy';image.decoding='async';row.append(image);}}catch(_){}}
      const info=node('div',null,'yu-detail-product-info');info.append(heading(itemName(l),4),paragraph([l.variantTitle&&l.variantTitle!=='Default Title'?l.variantTitle:'',`Quantity: ${l.quantity}`].filter(Boolean).join(' · '),'yu-member-fineprint yu-detail-product-meta'));row.append(info,node('span',money(l.totalPrice),'yu-detail-product-price'));items.append(row);
    }
    if(!lines.length)items.append(paragraph('Order item information is unavailable. Contact YŪ SEI for help.'));
    main.append(items);
    const cols=node('div',null,'yu-member-profile-grid yu-classic-detail-summary'),summary=node('div',null,'yu-classic-card yu-classic-totals'),dl=node('dl',null,'yu-account-totals');summary.dataset.yuOrderTotals='';summary.append(heading('Payment summary'));
    for(const [label,value] of [['Subtotal (Shopify)',o.subtotal],['Shipping',o.totalShipping],['Tax',o.totalTax],['Current order total',o.totalPrice]])if(value){const pair=node('div');pair.append(node('dt',label),node('dd',money(value)));dl.append(pair);}summary.append(dl);cols.append(summary);
    renderOrderRefundDetails(o,cols);
    if(o.shippingAddress){const address=node('div',null,'yu-classic-card yu-classic-shipping-address');address.append(heading('Delivery address'));const a=node('address');for(const line of o.shippingAddress.formatted)a.append(node('div',line));address.append(a);cols.append(address);}main.append(cols);layout.append(main,delivery);root.append(layout);
    const shipments=Array.isArray(o.fulfillments?.nodes)?o.fulfillments.nodes.filter(Boolean):[];
    if(!shipments.length){const empty=node('div',null,'yu-delivery-card yu-classic-card');empty.append(paragraph('DELIVERY','yu-delivery-eyebrow'),heading('Delivery updates'),paragraph(o.cancelledAt?'This order was cancelled.':'No shipment information is available yet.'));delivery.append(empty);}
    for(const f of shipments){
      const box=node('div',null,'yu-account-shipment yu-delivery-card yu-classic-card'),statusTitle=heading(f.latestShipmentStatus||f.status?human(f.latestShipmentStatus||f.status):'Status unavailable');statusTitle.classList.add('yu-delivery-status');box.append(paragraph('DELIVERY','yu-delivery-eyebrow'),statusTitle);
      const estimate=date(f.estimatedDeliveryAt);box.append(paragraph(estimate?`Estimated delivery: ${estimate}`:'Estimated delivery is not available yet.','yu-member-fineprint yu-delivery-estimate'));
      renderRecordItems(f.fulfillmentLineItems,box,'shipment',o.name);
      for(const t of Array.isArray(f.trackingInformation)?f.trackingInformation.filter(Boolean):[]){
        const trackingMeta=node('dl',null,'yu-delivery-meta');
        if(t.company){const pair=node('div');pair.append(node('dt','Carrier'),node('dd',t.company));trackingMeta.append(pair);}
        if(t.number){const pair=node('div');pair.append(node('dt','Tracking number'),node('dd',t.number));trackingMeta.append(pair);}
        if(trackingMeta.childNodes.length)box.append(trackingMeta);
        const courier=external('Track with the courier ↗',t.url);if(courier){courier.className='yu-member-button yu-member-button--compact yu-delivery-track';box.append(courier);}
      }
      const events=(Array.isArray(f.events?.nodes)?f.events.nodes.filter(Boolean):[]).slice().sort((a,b)=>{const left=Date.parse(a.happenedAt),right=Date.parse(b.happenedAt);return (Number.isFinite(right)?right:-Infinity)-(Number.isFinite(left)?left:-Infinity)||0;});
      if(events.length){const ul=node('ol',null,'yu-account-timeline yu-delivery-timeline');for(const e of events){const li=node('li',null,'yu-delivery-timeline-item'),label=node('span',human(e.status),'yu-delivery-event-status'),when=date(e.happenedAt,true),time=node('time',when||'Time unavailable');if(when)time.dateTime=e.happenedAt;li.append(label,time);ul.append(li);}box.append(ul);}
      if(f.events?.pageInfo?.hasNextPage!==false)box.append(paragraph('The complete shipment history is not shown here. Contact YŪ SEI for further delivery updates.','yu-member-fineprint'),helpLink('Ask about shipment history','Order enquiry',o.name));
      delivery.append(box);
    }
    if(o.fulfillments?.pageInfo?.hasNextPage)delivery.append(helpLink('Contact us for additional shipments', 'Order enquiry',o.name));
    const actions=node('div',null,'yu-account-form-actions');renderOrderReturnActions(o,actions);actions.append(helpLink('Need help with this order?','Order enquiry',o.name));
    if(!o.cancelledAt&&['UNFULFILLED','PARTIALLY_FULFILLED','ON_HOLD','SCHEDULED'].includes(o.fulfillmentStatus))actions.append(helpLink('Request cancellation','Cancellation request',o.name));root.append(actions);
  }

  // ----- 02.7: read-only pricing presentation. No account-session authority here. -----
  const PRICE_SCALE = 1000000n;
  function pricingError() {
    return new Error('The complete discount breakdown could not be verified.');
  }
  function priceUnits(value, expectedCurrency) {
    if (!value || typeof value.amount !== 'string' ||
        typeof value.currencyCode !== 'string' || !/^[A-Z]{3}$/.test(value.currencyCode) ||
        (expectedCurrency && value.currencyCode !== expectedCurrency)) throw pricingError();
    // Exact decimal arithmetic only. Reject missing, nonfinite or negative values.
    const m = /^(\d{1,14})(?:\.(\d{1,18}))?$/.exec(value.amount);
    if (!m || /[1-9]/.test((m[2] || '').slice(6))) throw pricingError();
    return BigInt(m[1]) * PRICE_SCALE + BigInt((m[2] || '').slice(0,6).padEnd(6,'0'));
  }
  function priceMoney(units, currency) {
    if (typeof units !== 'bigint' || units < 0n) throw pricingError();
    return {amount: `${units / PRICE_SCALE}.${String(units % PRICE_SCALE).padStart(6,'0')}`, currencyCode: currency};
  }
  function discountName(application) {
    const value = application?.__typename === 'DiscountCodeApplication' ? application.code : application?.title;
    return typeof value === 'string' && value.trim() ? value.trim().slice(0,300) : 'Discount';
  }
  function groupedDiscounts(allocations, currency, target) {
    if (!Array.isArray(allocations)) throw pricingError();
    const groups = new Map();
    let total = 0n;
    for (const allocation of allocations) {
      const amount = priceUnits(allocation?.allocatedAmount, currency);
      const application = allocation?.discountApplication;
      if (!application || application.targetType !== target) throw pricingError();
      const label = discountName(application);
      const key = JSON.stringify([application.__typename || '', label, target]);
      if (amount > 0n) {
        const group = groups.get(key) || {label, units: 0n};
        group.units += amount;
        groups.set(key, group);
      }
      total += amount;
    }
    return {total, rows: [...groups.values()].map(g => ({label:g.label, amount:priceMoney(g.units,currency)}))};
  }
  function buildPricingModel(order) {
    const currency = order?.totalPrice?.currencyCode;
    if (typeof currency !== 'string') throw pricingError();
    const total = priceUnits(order.totalPrice, currency);
    const subtotal = priceUnits(order.subtotal, currency);
    const shipping = priceUnits(order.totalShipping, currency);
    const lines = order.lineItems;
    if (!lines || !Array.isArray(lines.nodes) || lines.pageInfo?.hasNextPage !== false) throw pricingError();
    let gross = 0n;
    const allocations = [], ids = new Set();
    for (const line of lines.nodes) {
      if (typeof line.id !== 'string' || !line.id || ids.has(line.id) ||
          !Number.isInteger(line.quantity) || line.quantity < 0 ||
          !Array.isArray(line.discountAllocations)) throw pricingError();
      ids.add(line.id);
      gross += priceUnits(line.totalPrice,currency);
      allocations.push(...line.discountAllocations);
    }
    const items = groupedDiscounts(allocations,currency,'LINE_ITEM');
    const shippingDiscounts = groupedDiscounts(order.shippingDiscountAllocations,currency,'SHIPPING_LINE');
    // Only a supported exact reconciliation permits an additive subtotal.
    // Refunds/edits can change current totals without changing historic allocations.
    const itemReconciles = gross >= items.total && gross - items.total === subtotal;
    let shippingReconciles = false, originalShipping = null;
    if (order.shippingLine?.originalPrice) {
      const before = priceUnits(order.shippingLine.originalPrice,currency);
      originalShipping = priceMoney(before,currency);
      shippingReconciles = before >= shippingDiscounts.total && before - shippingDiscounts.total === shipping;
    }
    const optional = {};
    for (const [key,value] of [['tax',order.totalTax],['duties',order.totalDuties],['tip',order.totalTip]]) {
      optional[key] = value == null ? null : priceMoney(priceUnits(value,currency),currency);
    }
    // NEVER derive total by adding subtotal/shipping/tax: tax inclusion and order
    // adjustments are Shopify's responsibility. Refunds remain a separate record.
    return {
      currency, gross:priceMoney(gross,currency), subtotal:priceMoney(subtotal,currency),
      shipping:priceMoney(shipping,currency), total:priceMoney(total,currency),
      items, shippingDiscounts, itemReconciles, shippingReconciles,
      originalShipping, ...optional
    };
  }
  function totalsPair(list, label, value, negative=false) {
    const pair=node('div');
    pair.append(node('dt',label),node('dd',negative?`−${money(value)}`:money(value)));
    list.append(pair);
  }
  function renderOrderPricing(model, target) {
    const list=node('dl',null,'yu-account-totals');
    const hasItemsDiscount=model.items.total>0n,hasShippingDiscount=model.shippingDiscounts.total>0n;
    if (model.itemReconciles && hasItemsDiscount) {
      totalsPair(list,'Items before discounts',model.gross);
      for (const row of model.items.rows) totalsPair(list,`Discount - ${row.label}`,row.amount,true);
      totalsPair(list,'Subtotal after discounts',model.subtotal);
    } else {
      totalsPair(list,hasItemsDiscount?'Current subtotal (Shopify)':'Subtotal',model.subtotal);
    }
    if (hasShippingDiscount && model.shippingReconciles) {
      totalsPair(list,'Shipping before discounts',model.originalShipping);
      for (const row of model.shippingDiscounts.rows) totalsPair(list,`Shipping discount - ${row.label}`,row.amount,true);
      totalsPair(list,'Shipping after discounts',model.shipping);
    } else totalsPair(list,'Shipping',model.shipping);
    if (model.tax) totalsPair(list,'Tax',model.tax);
    if (model.duties && priceUnits(model.duties)>0n) totalsPair(list,'Duties',model.duties);
    if (model.tip && priceUnits(model.tip)>0n) totalsPair(list,'Tip',model.tip);
    totalsPair(list,'Current order total',model.total);
    const fragments=[heading('Payment summary'),list];
    // Non-additive history, rather than treating a refund/removed item as a discount.
    const records=[];
    if (!model.itemReconciles && hasItemsDiscount) records.push(...model.items.rows.map(r=>({...r,label:`Item/order discount - ${r.label}`})));
    if (!model.shippingReconciles && hasShippingDiscount) records.push(...model.shippingDiscounts.rows.map(r=>({...r,label:`Shipping discount - ${r.label}`})));
    if (records.length) {
      const box=node('section',null,'yu-account-recorded-discounts');box.append(heading('Recorded discounts',4));
      const recorded=node('dl',null,'yu-account-totals');
      for(const row of records)totalsPair(recorded,row.label,row.amount,true);
      box.append(recorded,paragraph('These are discount allocations recorded by Shopify. They are shown separately and are not additional deductions from the current subtotal or total above.','yu-member-fineprint'));
      fragments.push(box);
    }
    // If tax is reported, avoid claiming whether it is included from an unavailable flag.
    if(model.tax && priceUnits(model.tax)>0n)fragments.push(paragraph('Tax is shown as reported by Shopify and may already be included in the amounts above. The order total is not recalculated here.','yu-member-fineprint'));
    target.replaceChildren(...fragments);
    target.dataset.yuPricingState='loaded';
  }
  async function enhanceOrderTotals(id, root, orderName='') {
    const target=root.querySelector('[data-yu-order-totals]');
    if (!target) return;
    const epoch=generation,customerId=session?.customerId;
    if (!customerId || !target.isConnected) return;
    const stillCurrent=()=>epoch===generation && session?.customerId===customerId && target.isConnected;
    const notice=paragraph('Loading discount breakdown…','yu-member-fineprint');notice.setAttribute('role','status');target.append(notice);target.dataset.yuPricingState='loading';
    try {
      let after=null,pricing=null,pages=0;
      const cursors=new Set();
      do {
        if(++pages>50)throw pricingError();
        const response=await gql(Q.pricing,{id,after});
        if(!stillCurrent())return;
        const part=response.order;
        if(!part || part.id!==id || typeof part.updatedAt!=='string' || !part.updatedAt || !Array.isArray(part.lineItems?.nodes))throw pricingError();
        if(!pricing)pricing={...part,lineItems:{...part.lineItems,nodes:[...part.lineItems.nodes]}};
        else {
          if(part.updatedAt!==pricing.updatedAt)throw pricingError();
          pricing.lineItems.nodes.push(...part.lineItems.nodes);
          pricing.lineItems.pageInfo=part.lineItems.pageInfo;
        }
        const info=part.lineItems.pageInfo;
        if(typeof info?.hasNextPage!=='boolean')throw pricingError();
        after=info.hasNextPage?info.endCursor:null;
        if(info.hasNextPage && (typeof after!=='string'||!after||cursors.has(after)))throw pricingError();
        if(after)cursors.add(after);
      } while(after);
      const model=buildPricingModel(pricing);
      if(stillCurrent())renderOrderPricing(model,target);
    } catch(error) {
      if(!stillCurrent())return;
      target.dataset.yuPricingState='unavailable';
      notice.textContent='The discount breakdown could not be loaded or reconciled. The amounts above remain Shopify’s reported totals. Refresh your account or contact YŪ SEI before relying on the discount breakdown.';
      target.append(helpLink('Ask about this order total','Order enquiry',orderName));
      // Do not expose response bodies, customer IDs, monetary data, tokens, or URLs.
      if(config.previewNotice)target.append(paragraph('[YU-PRICING: UNAVAILABLE] No price or discount has been invented.','yu-member-fineprint'));
    }
  }
  // ----- End 02.7 pricing presentation. -----

  // Display mapping only: never changes the Shopify return record or refund state.
  function returnStatusLabel(value) {
    switch (value) {
      case 'REQUESTED': return 'Return requested';
      case 'OPEN': return 'Return approved';
      case 'DECLINED': return 'Return request declined';
      case 'CANCELED': return 'Return cancelled';
      default: return human(value);
    }
  }
  function refundAmount(value){
    try{return {text:money(value),positive:priceUnits(value)>0n};}catch(_){return null;}
  }
  function refundTransactionStatus(status){
    switch(status){
      case 'PENDING':return 'Refund transaction pending';
      case 'PENDING_AUTHENTICATION':return 'Refund transaction awaiting authentication';
      case 'SUCCESS':return 'Refund transaction successful';
      case 'FAILURE':return 'Refund transaction failed';
      case 'ERROR':return 'Refund transaction error';
      default:return 'Refund transaction status unavailable';
    }
  }
  function renderRefundRecords(o,section){
    const refunds=Array.isArray(o.refunds)?o.refunds.filter(Boolean):[];
    const transactions=Array.isArray(o.transactions)?o.transactions.filter(t=>t?.kind==='REFUND'):[];
    let unconfirmed=!Array.isArray(o.refunds)||!Array.isArray(o.transactions);
    if(refunds.length){
      section.append(heading('Refund records',4));
      for(const refund of refunds){
        const amount=refundAmount(refund.totalRefunded);
        const details=[refund.createdAt?date(refund.createdAt):'',refund.returnName||''].filter(Boolean).join(' · ');
        if(amount?.positive)section.append(paragraph(`Refund amount recorded: ${amount.text}${details?' · '+details:''}`));
        else{unconfirmed=true;section.append(paragraph(`Refund record found${details?' · '+details:''}. A completed refunded amount is not confirmed here yet.`));}
      }
    }
    if(transactions.length){
      section.append(heading('Refund transaction updates',4));
      for(const transaction of transactions){
        const amount=refundAmount(transaction.transactionAmount?.presentmentMoney),status=transaction.status;
        section.append(paragraph(`${refundTransactionStatus(status)}: ${amount?amount.text:'Amount unavailable'}${transaction.createdAt?' · '+date(transaction.createdAt):''}`));
        if(!amount||!['SUCCESS','PENDING','PENDING_AUTHENTICATION'].includes(status))unconfirmed=true;
      }
      if(refunds.length)section.append(paragraph('Refund records and transaction updates are shown separately. Do not add these amounts together.','yu-member-fineprint'));
    }
    if(refunds.length||transactions.length)section.append(paragraph('A pending refund is not complete. Bank processing can take additional time after a successful refund transaction. These records do not confirm that money has reached your bank.','yu-member-fineprint'));
    else section.append(paragraph('No refund details are available here yet. A completed return does not itself confirm a refund.','yu-member-fineprint'));
    if(unconfirmed)section.append(paragraph('Some refund details could not be confirmed here. Refresh your account for the latest information, or contact YŪ SEI to confirm the refund status.','yu-member-fineprint'),helpLink('Ask about this refund','Return enquiry',o.name));
  }
  function returnProductImage(orderId,lineId){
    const line=orders.find(o=>o.id===orderId)?.lineItems?.nodes?.find(l=>l.id===lineId);
    if(!line?.image?.url)return null;
    try{const url=new URL(line.image.url);if(!['https:','http:'].includes(url.protocol))return null;
      const image=node('img',null,'yu-return-product-image');image.src=url.href;image.alt=line.image.altText||itemName(line);image.loading='lazy';image.decoding='async';return image;
    }catch(_){return null;}
  }
  function renderReturnRecordItems(connection,root,orderId,orderName){
    const items=Array.isArray(connection?.nodes)?connection.nodes.filter(Boolean):[];let unavailable=!items.length;
    for(const item of items){
      const line=item.lineItem,quantity=item.quantity,knownQuantity=Number.isInteger(quantity)&&quantity>=0;if(!line||!knownQuantity)unavailable=true;
      const row=node('div',null,'yu-return-product-row'),image=returnProductImage(orderId,line?.id),info=node('div',null,'yu-return-product-info');if(image)row.append(image);
      info.append(heading(itemName(line),5));if(line?.variantTitle&&line.variantTitle!=='Default Title')info.append(paragraph(line.variantTitle,'yu-member-fineprint'));
      info.append(paragraph(knownQuantity?`Quantity: ${quantity}`:'Quantity unavailable','yu-member-fineprint'));row.append(info);root.append(row);
    }
    if(unavailable||connection?.pageInfo?.hasNextPage!==false){
      const message=connection?.pageInfo?.hasNextPage===true?'Some items in this return are not shown. Contact YŪ SEI for the complete item details.':'The complete item details for this return could not be confirmed. Contact YŪ SEI for help.';
      root.append(paragraph(message,'yu-member-fineprint'),helpLink('Ask about items in this return','Return enquiry',orderName));
    }
  }
  function openOrderFromReturn(orderId,focusRefunds=false){
    if(!session || !customer || session.customerId!==customer.id || !numericOrderId(orderId))return;
    adoptDestination({kind:'id',ref:orderId,tab:'orders',returnRef:''});
    void openAccountDestination({focusRefunds});
  }
  // Only navigate from a return that was fetched for this signed-in customer.
  function openReturnFromOrder(orderId,returnId=''){
    if(!session || !customer || session.customerId!==customer.id || !numericOrderId(orderId))return;
    const returnRef=returnId ? plainReference(returnId) : '';
    if(returnId && !returnRef)return;
    adoptDestination({kind:'id',ref:orderId,tab:'returns',returnRef,viewReturnOnly:true});
    void openAccountDestination({viewReturnOnly:true});
  }
  function renderOrderReturnActions(o,actions){
    const connection=o.returns, nodes=connection?.nodes;
    const knownStatuses=['REQUESTED','OPEN','CLOSED','DECLINED','CANCELED'];
    const complete=Array.isArray(nodes) && connection.pageInfo?.hasNextPage===false &&
      nodes.every(r=>r && typeof r.id==='string' && plainReference(r.id) && knownStatuses.includes(r.status));
    const active=Array.isArray(nodes)?nodes.filter(r=>r && ['REQUESTED','OPEN'].includes(r.status) && plainReference(r.id)):[];
    if(active.length){
      for(const r of active){const label=active.length>1 && r.name ? 'View return '+r.name : 'View return';
        actions.append(btn(label,()=>openReturnFromOrder(o.id,r.id),'yu-member-button yu-member-button--compact'));
      }
      if(!complete)actions.append(paragraph('Some return details could not be confirmed.','yu-member-fineprint'));
    } else if(complete){
      actions.append(btn('Check return eligibility',()=>{space.selectTab('returns',false,true);openReturn(o.id);},'yu-member-button yu-member-button--compact'));
    } else {
      actions.append(btn('View returns',()=>openReturnFromOrder(o.id),'yu-member-button yu-member-button--compact'));
      actions.append(paragraph('Return status could not be fully confirmed.','yu-member-fineprint'));
    }
  }
  function renderOrderRefundDetails(o,root){
    // Refund records and payment transactions are different Shopify data. Do
    // not add them together or infer a relationship between individual rows.
    const card=node('section',null,'yu-classic-card yu-order-refund-card yu-return-refund-details');
    card.append(heading('Refund'));
    const refunds=Array.isArray(o.refunds)?o.refunds:[],transactions=Array.isArray(o.transactions)?o.transactions:[];
    let uncertain=!Array.isArray(o.refunds)||!Array.isArray(o.transactions)||
      refunds.some(r=>!r || typeof r!=='object')||transactions.some(t=>!t || typeof t!=='object' || typeof t.kind!=='string');
    const validRefunds=refunds.filter(r=>r && typeof r==='object');
    const refundTx=transactions.filter(t=>t && t.kind==='REFUND');
    const rows=node('dl',null,'yu-order-refund-rows');
    const addRow=(label,value)=>{const row=node('div');row.append(node('dt',label),node('dd',value));rows.append(row);};
    let hasSuccessful=false,needsAttention=false;
    if(refundTx.length){
      for(const tx of refundTx){
        const amount=refundAmount(tx.transactionAmount?.presentmentMoney);
        const validAmount=Boolean(amount?.positive);
        let label;
        switch(tx.status){
          case 'SUCCESS':label=validAmount?'Refund processed':'Refund status unavailable';hasSuccessful=hasSuccessful||validAmount;break;
          case 'PENDING':label='Refund pending';break;
          case 'PENDING_AUTHENTICATION':label='Refund awaiting confirmation';break;
          case 'FAILURE':case 'ERROR':label='Refund needs attention';needsAttention=true;break;
          default:label='Refund status unavailable';uncertain=true;
        }
        if(!validAmount)uncertain=true;
        addRow(label,validAmount?amount.text:'Amount unavailable');
      }
      if(validRefunds.some(r=>!refundAmount(r.totalRefunded)?.positive))uncertain=true;
    }else if(validRefunds.length){
      for(const refund of validRefunds){
        const amount=refundAmount(refund.totalRefunded);
        if(!amount?.positive)uncertain=true;
        addRow('Refund recorded',amount?.positive?amount.text:'Amount unavailable');
      }
      card.append(rows,paragraph('Processing status unavailable.','yu-member-fineprint yu-order-refund-note'));
    }else{
      card.append(paragraph(uncertain?'Refund details could not be confirmed.':'No refund details available yet.','yu-order-refund-empty'));
    }
    if(refundTx.length)card.append(rows);
    if(hasSuccessful)card.append(paragraph('It may take time to appear in your account.','yu-member-fineprint yu-order-refund-note'));
    if(uncertain)card.append(paragraph('Some refund information could not be confirmed.','yu-member-fineprint yu-order-refund-note'));
    if(uncertain||needsAttention)card.append(helpLink('Contact us about this refund','Return enquiry',o.name));
    if(validRefunds.length||refundTx.length||uncertain){
      const more=node('details',null,'yu-order-refund-more'),summary=node('summary','More details');
      const content=node('div',null,'yu-return-refund-content');renderRefundRecords(o,content);more.append(summary,content);card.append(more);
    }
    root.append(card);
  }
  function renderReturnRecords(o,root,returnView=false){
    const section=node('section',null,'yu-account-flow-section yu-return-overview-order');
    const main=node('div',null,'yu-return-overview-main');section.append(main);
    const rs=Array.isArray(o.returns?.nodes)?o.returns.nodes.filter(Boolean):[];
    if(!rs.length)main.append(heading(o.name?`Order ${o.name}`:'Returns & refunds'),paragraph(Array.isArray(o.returns?.nodes)&&o.returns.pageInfo?.hasNextPage===false?'No return requests are recorded for this order.':'Return records could not be fully confirmed here.'));
    for(const r of rs){
      const card=node('article',null,'yu-account-return-card yu-return-record-card'),top=node('div',null,'yu-return-record-header');
      const identity=node('div',null,'yu-return-record-identity');
      const title=heading(null,4),titleText=o.name?`Order ${o.name}`:'Return';
      const orderUrl=returnView ? linkedAccountUrl(o.id,'orders') : null;
      if(orderUrl){
        const orderLink=node('a',titleText,'yu-member-text-link');orderLink.href=orderUrl;
        orderLink.style.font='inherit';orderLink.style.display='inline';orderLink.style.minHeight='0';
        orderLink.addEventListener('click',e=>{
          if(e.button!==0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)return;
          e.preventDefault();openOrderFromReturn(o.id);
        });
        title.append(orderLink);
      }else title.textContent=titleText;
      identity.append(title);
      const requested=date(r.createdAt);if(requested)identity.append(paragraph(`Requested ${requested}`,'yu-member-fineprint'));if(r.name)identity.append(paragraph(r.name,'yu-member-fineprint'));
      top.append(identity,paragraph(returnStatusLabel(r.status),'yu-return-record-status'));card.append(top);
      card.dataset.yuReturnId=r.id;card.dataset.yuReturnName=r.name;addDestinationTestLinks(card,o.id,r.id);
      renderReturnRecordItems(r.returnLineItems,card,o.id,o.name);
      const timeline=node('ol',null,'yu-return-record-timeline');
      const current=node('li');current.append(paragraph(returnStatusLabel(r.status)));timeline.append(current);
      if(requested){const created=node('li');created.append(paragraph('Return requested'),paragraph(requested,'yu-member-fineprint'));timeline.append(created);}card.append(timeline);
      const group=node('div',null,'yu-return-inline-label');let labelCount=0;
      for(const d of r.reverseDeliveries?.nodes||[]){const v=d?.deliverable;if(!v)continue;
        const link=external('View return label',v.label?.publicFileUrl);if(link){labelCount++;link.classList.add('yu-member-button','yu-member-button--compact');group.append(link);}
        if(v.tracking){const tracking=[v.tracking.carrierName,v.tracking.trackingNumber].filter(Boolean).join(' · ');if(tracking)group.append(paragraph(tracking,'yu-member-fineprint'));appendLink(group,'Track the return ↗',v.tracking.trackingUrl);}
      }
      group.append(paragraph(labelCount?'Return label available. Follow the carrier instructions on your label.':'No usable return label is available in these records. Contact YŪ SEI before sending items back.','yu-member-fineprint'));
      if(r.reverseDeliveries?.pageInfo?.hasNextPage!==false)group.append(paragraph('The complete return label and tracking information is not shown here. Contact YŪ SEI before sending any remaining items.','yu-member-fineprint'),helpLink('Ask about return labels and tracking','Return enquiry',o.name));
      card.append(group);
      if(returnView && orderUrl)card.append(btn('View refund details',()=>openOrderFromReturn(o.id,true),'yu-member-text-button'));
      main.append(card);
    }
    if(o.returns?.pageInfo?.hasNextPage!==false)main.append(helpLink('Contact us for additional return records','Return enquiry',o.name));
    // Refunds belong to the order; never pair or sum them with a guessed return.
    if(!returnView){
      const refundDetails=node('details',null,'yu-return-refund-details');
      const summary=node('summary',o.name?`Refund information for order ${o.name}`:'Refund information');
      const refunds=node('div',null,'yu-return-refund-content');renderRefundRecords(o,refunds);
      refundDetails.append(summary,refunds);section.append(refundDetails);
    }
    root.append(section);
  }
  function renderReturnList(){
    returnGeneration++;
    const root=$('[data-yu-returns-content]');root.replaceChildren();root.dataset.yuPrivate='';root.dataset.yuReturnView='overview';root.dataset.yuReturnOverviewState='idle';
    delete root.dataset.yuReturnOverviewResult;
    if(!orders.length){
      const empty=node('div',null,'yu-returns-empty');
      empty.append(paragraph('YOUR RETURNS','yu-returns-eyebrow'),heading('No returns to show.',3),paragraph('No orders are showing for this account yet.'),btn('View orders',()=>space.selectTab('orders',false,true),'yu-member-button yu-member-button--compact'));
      root.append(empty);return;
    }
    const notice=paragraph('','yu-member-fineprint');notice.dataset.yuReturnOverviewNotice='';
    const emptyHeading=node('div',null,'yu-returns-overview-empty-heading');emptyHeading.hidden=true;emptyHeading.append(paragraph('YOUR RETURNS','yu-returns-eyebrow'),heading('No returns to show.',3));root.append(emptyHeading);
    const list=node('div',null,'yu-return-overview-list');list.dataset.yuReturnOverviewList='';root.append(notice,list);
    const start=node('div',null,'yu-return-start-card'),startCopy=node('div');startCopy.append(heading('Need to start a return?',4),paragraph('Open an order to check eligible items and request a return.'));start.append(startCopy,btn('View orders',()=>space.selectTab('orders',false,true),'yu-member-button yu-member-button--compact yu-return-start-orders'));root.append(start);
    if(pageInfo?.hasNextPage){root.append(paragraph('Earlier orders may contain additional returns. Load earlier orders to check them.','yu-member-fineprint'));const more=btn('Load earlier orders',()=>withButton(more,()=>loadOrders(true)));root.append(more);}
    ensureReturnOverview();
  }
  let returnGeneration=0;
  let returnOverviewReads=0;
  const returnOverviewWaiters=[];
  async function readReturnOverview(id,valid){
    // Shared semaphore survives tab re-entry while old requests are still draining.
    while(returnOverviewReads>=2){await new Promise(resolve=>returnOverviewWaiters.push(resolve));if(!valid())return null;}
    if(!valid())return null;returnOverviewReads++;
    try{return await gql(Q.returnOverview,{id});}
    finally{returnOverviewReads--;for(const resume of returnOverviewWaiters.splice(0))resume();}
  }
  function ensureReturnOverview(){
    const root=$('[data-yu-returns-content]');
    if(!root||space.dataset.yuAccountView!=='returns'||root.dataset.yuReturnView!=='overview'||root.dataset.yuReturnOverviewState!=='idle'||destination||destinationInvalid||!orders.length||!session||!customer)return;
    const expectedId=session.customerId,epoch=generation,current=returnGeneration,destinationToken=destinationSerial;
    if(customer.id!==expectedId)return;
    const list=root.querySelector('[data-yu-return-overview-list]'),notice=root.querySelector('[data-yu-return-overview-notice]');if(!list||!notice)return;
    const valid=()=>!pageLeaving&&epoch===generation&&current===returnGeneration&&destinationToken===destinationSerial&&!destination&&session?.customerId===expectedId&&customer?.id===expectedId&&$('[data-yu-returns-content]')===root&&root.isConnected&&root.contains(list)&&root.dataset.yuReturnView==='overview'&&space.dataset.yuAccountView==='returns';
    list.replaceChildren();root.dataset.yuReturnOverviewState='loading';notice.textContent='Loading return records for your loaded orders…';
    // At most two reads at a time. Nothing is stored in browser storage or a private cache.
    const queue=orders.map(o=>{const slot=node('div',null,'yu-return-overview-slot');list.append(slot);return {id:o.id,name:o.name,slot};});
    let active=0,failed=0,records=0;
    const finish=()=>{if(!valid()||active||queue.length)return;root.dataset.yuReturnOverviewState='ready';const empty=!failed&&!records;root.dataset.yuReturnOverviewResult=empty?'empty':failed?'partial':'records';const h=root.querySelector('.yu-returns-overview-empty-heading');if(h)h.hidden=!empty;notice.textContent=failed?'Some return records could not be loaded. Retry the affected order below.':records?'':'No return records were found in the successfully checked loaded orders.';notice.hidden=!failed&&!!records;};
    const pump=()=>{if(!valid())return;while(active<2&&queue.length){const item=queue.shift();active++;read(item).finally(()=>{active--;if(valid()){pump();finish();}});}finish();};
    const read=async item=>{
      try{
        const data=await readReturnOverview(item.id,valid);if(!valid())return;
        const o=data.order;if(!o||o.id!==item.id||!Array.isArray(o.returns?.nodes)||typeof o.returns.pageInfo?.hasNextPage!=='boolean')throw new Error('Return records could not be confirmed for this order.');
        const hasRecords=o.returns.nodes.length||o.returns.pageInfo.hasNextPage;
        item.slot.replaceChildren();
        if(hasRecords){records++;renderReturnRecords(o,item.slot,true);}
      }catch(error){
        if(!valid())return;failed++;
        item.slot.replaceChildren(paragraph(`Return information for ${item.name||'this order'} could not be loaded.`),paragraph(errorText(error),'yu-member-fineprint'));
        const retry=btn('Retry this order',()=>{if(!valid())return;failed--;item.slot.replaceChildren();queue.push(item);root.dataset.yuReturnOverviewState='loading';notice.textContent='Loading return records…';pump();});item.slot.append(retry);
      }
    };
    pump();
  }
  space.addEventListener('yu:account-tab',event=>{
    const root=$('[data-yu-returns-content]');if(!root)return;
    if(event.detail?.name!=='returns'){
      if(root.dataset.yuReturnView==='overview'&&root.dataset.yuReturnOverviewState==='loading'){returnGeneration++;root.dataset.yuReturnOverviewState='idle';}
      if(root.dataset.yuReturnView==='request'&&root.dataset.yuReturnRequestState==='loading'){returnGeneration++;root.dataset.yuReturnView='interrupted';}
      return;
    }
    if(!destination&&root.dataset.yuReturnView==='interrupted'){renderReturnList();return;}
    // A caller may immediately open a request after selecting Returns.
    Promise.resolve().then(ensureReturnOverview);
  });
  async function openReturn(id,options={}){
    const current=++returnGeneration,epoch=generation,expectedId=session?.customerId,destinationToken=destinationSerial,root=$('[data-yu-returns-content]');
    const view=node('div',null,'yu-return-request-view');view.append(paragraph('Loading return information…'));root.replaceChildren(view);root.dataset.yuPrivate='';root.dataset.yuReturnView='request';root.dataset.yuReturnRequestState='loading';
    const valid=()=>!pageLeaving&&current===returnGeneration&&epoch===generation&&destinationToken===destinationSerial&&session?.customerId===expectedId&&customer?.id===expectedId&&Boolean(expectedId)&&$('[data-yu-returns-content]')===root&&root.isConnected&&root.contains(view);
    const back=()=>{returnGeneration++;clearDestination();renderReturnList();};
    try{const data=await gql(Q.detail,{id});if(!valid())return;const o=data.order;if(!o||o.id!==id)throw new Error('This order is not available for this account.');
      const header=node('div',null,'yu-return-request-header');header.append(heading(options.viewReturnOnly?'Your returns':'Request a return',1),paragraph([o.name,o.cancelledAt?'Cancelled':human(o.fulfillmentStatus)].filter(Boolean).join(' · ')),btn('Back to returns',back));view.replaceChildren(header);
      if(options.viewReturnOnly){
        renderReturnRecords(o,view,true);
      }else{
        const eligibility=node('div',null,'yu-return-request-card');eligibility.append(paragraph('Checking eligible items…'));view.append(eligibility);
        // Keep existing record IDs/links available for direct return links and later refreshes.
        renderReturnRecords(o,view,true);
        try{const d=await gql(Q.eligibility,{id});if(!valid())return;if(d.order?.id!==id)throw new Error('Return eligibility could not be verified for this order.');renderReturnForm(o,d.order.returnInformation,eligibility);}catch(e){if(!valid())return;eligibility.replaceChildren(paragraph('Return eligibility is temporarily unavailable. YŪ SEI can help with your request.'),helpLink('Contact YŪ SEI','Return enquiry',o.name));}
      }
      if(valid())root.dataset.yuReturnRequestState='ready';
    }catch(e){if(!valid())return;root.dataset.yuReturnRequestState='ready';view.replaceChildren(paragraph(errorText(e)),btn('Back to returns',back));}
  }
  function renderReturnForm(o,info,root){
    const steps=node('div','1  Select items   /   2  Review request','yu-return-request-steps');steps.dataset.yuReturnStep='select';
    root.replaceChildren(steps,heading('What would you like to return?'));
    const eligible=info?.returnableLineItems?.nodes||[];
    if(!eligible.length){root.append(paragraph('No items are currently eligible for an online return request. For a damaged or incorrect item, or to discuss your order, contact YŪ SEI.'),helpLink('Report a problem or discuss a return','Return enquiry',o.name));return;}
    if(info.returnableLineItems.pageInfo?.hasNextPage){root.append(paragraph('This order needs a personal review. Contact YŪ SEI to arrange your return.'),helpLink('Contact YŪ SEI','Return enquiry',o.name));return;}
    if(info.hasRestockingFee||info.hasReturnShippingFee)root.append(paragraph('A return-shipping charge or restocking fee may apply under the returns policy. We will confirm applicable charges before you send items back.','yu-account-notice'));
    if(info.returnShippingMethodNote)root.append(paragraph(info.returnShippingMethodNote));
    writeNotice(root);const form=node('form',null,'yu-account-return-form'), rows=[];
    for(const item of eligible){const definitions=(item.lineItem.suggestedReturnReasonDefinitions?.nodes||[]).filter(d=>!d.deleted);if(!definitions.length||item.lineItem.suggestedReturnReasonDefinitions.pageInfo?.hasNextPage){root.append(helpLink(`Contact us to return ${itemName(item.lineItem)}`,'Return enquiry',o.name));continue;}
      const fs=node('fieldset',null,'yu-return-request-item'),legend=node('legend',itemName(item.lineItem));fs.append(legend);
      const head=node('div',null,'yu-return-request-item-head');
      const choose=node('label',null,'yu-account-checkbox'),check=node('input');check.type='checkbox';choose.append(check,node('span','Include this item','visually-hidden'));head.append(choose);
      const image=returnProductImage(o.id,item.lineItem.id);if(image)head.append(image);const itemInfo=node('div',null,'yu-return-request-item-info');itemInfo.append(paragraph(itemName(item.lineItem)),paragraph(`Eligible quantity: ${item.quantity}`,'yu-member-fineprint'));head.append(itemInfo);fs.append(head);
      const grid=node('div',null,'yu-account-form-grid');const quantity=field(grid,'quantity-'+rows.length,'Quantity','1',{type:'number'});quantity.min='1';quantity.max=String(item.quantity);quantity.step='1';
      const reason=field(grid,'reason-'+rows.length,'Reason','',{choices:[['','Choose a reason'],...definitions.map(d=>[d.id,d.name])]});
      const note=field(grid,'note-'+rows.length,'Additional information (optional) - 300 characters','',{textarea:true,max:300});note.closest('label').classList.add('yu-account-form-wide');
      const toggle=()=>{[quantity,reason,note].forEach(e=>e.disabled=!check.checked);reason.required=check.checked;quantity.required=check.checked;};check.addEventListener('change',toggle);toggle();fs.append(grid);form.append(fs);rows.push({check,quantity,reason,note,item,definitions});
    }
    if(!rows.length)return;
    const message=formMessage(),review=node('div',null,'yu-account-return-review');review.hidden=true;
    const submit=node('button','Review return request','yu-member-button yu-member-button--compact');submit.type='submit';submit.disabled=!config.allowWrites;
    let approved=null,busy=false,uncertain=false;
    const cancel=btn('Cancel',()=>{if(busy||uncertain)return;clearDestination();renderReturnList();},'yu-member-text-button');
    const actions=node('div',null,'yu-return-request-actions');actions.append(submit,cancel,paragraph('Requests are reviewed before approval.','yu-member-fineprint'));
    form.append(message,review,actions);root.append(form);
    form.addEventListener('input',()=>{if(busy)return;approved=null;review.hidden=true;steps.dataset.yuReturnStep='select';submit.textContent='Review return request';});
    form.addEventListener('submit',async e=>{
      e.preventDefault();if(busy||uncertain||!config.allowWrites)return;
      const requested=[];
      for(const r of rows.filter(x=>x.check.checked)){const n=Number(r.quantity.value);if(!Number.isInteger(n)||n<1||n>r.item.quantity||!r.definitions.some(d=>d.id===r.reason.value)){message.textContent='Check the quantity and reason for each selected item.';return;}requested.push({lineItemId:r.item.lineItem.id,quantity:n,returnReasonDefinitionId:r.reason.value,customerNote:r.note.value.trim()});}
      if(!requested.length){message.textContent='Choose at least one item to return.';return;}
      const signature=JSON.stringify(requested);
      if(approved!==signature){approved=signature;review.replaceChildren(paragraph('Please check your request before submitting.'));
        for(const r of rows.filter(x=>x.check.checked))review.append(paragraph(`${itemName(r.item.lineItem)} × ${r.quantity.value} - ${r.definitions.find(d=>d.id===r.reason.value)?.name}`));
        review.append(paragraph('Submitting creates a request for review. It does not approve the return or issue a refund. Please wait for our instructions before posting anything back.','yu-member-fineprint'));review.hidden=false;steps.dataset.yuReturnStep='review';message.textContent='';submit.textContent='Submit return request';return;}
      busy=true;submit.disabled=true;cancel.disabled=true;submit.setAttribute('aria-busy','true');
      try{const result=await mutate(Q.return,{orderId:o.id,requestedLineItems:requested},'orderRequestReturn');if(!result.return?.id){const e2=new Error('The return result could not be confirmed. Refresh before submitting again.');e2.uncertain=true;throw e2;}
        status(`Your return request ${result.return.name||''} has been received. YŪ SEI will review it.`);await openReturn(o.id);
      }catch(err){message.textContent=errorText(err);uncertain=Boolean(err.uncertain);if(uncertain)review.append(btn('Refresh return records',()=>openReturn(o.id)));}
      finally{busy=false;submit.disabled=uncertain||!config.allowWrites;cancel.disabled=uncertain;submit.removeAttribute('aria-busy');}
    });
  }
  // Customer Account API: authenticated customer's current email marketing state.
  // https://shopify.dev/docs/api/customer/latest/objects/CustomerEmailAddress
  // https://shopify.dev/docs/api/customer/latest/mutations/customerEmailMarketingSubscribe
  // https://shopify.dev/docs/api/customer/latest/mutations/customerEmailMarketingUnsubscribe
  function marketingAction(state) {
    if(state==='SUBSCRIBED'||state==='PENDING')return 'unsubscribe';
    if(state==='NOT_SUBSCRIBED'||state==='UNSUBSCRIBED')return 'subscribe';
    return null;
  }
  function renderMarketingPreference(root) {
    const state=customer.emailAddress?.marketingState,action=marketingAction(state);
    const descriptions={
      SUBSCRIBED:'You are subscribed to the World of YŪ SEI.',
      UNSUBSCRIBED:'You are unsubscribed from the World of YŪ SEI.',
      NOT_SUBSCRIBED:'You are not subscribed to the World of YŪ SEI.',
      PENDING:'Your subscription is pending. Check your email for any confirmation steps.',
      INVALID:'Your email preference is unavailable. Contact YŪ SEI for help.',
      REDACTED:'This email preference cannot be changed because the customer data has been erased.'
    };
    const card=node('div',null,'yu-member-profile-card');card.dataset.yuMarketingState=Object.hasOwn(descriptions,state)?state:'UNKNOWN';
    card.append(heading('Email preferences'),paragraph('Choose whether to subscribe to the World of YŪ SEI. Order and account emails are unaffected.'),paragraph(Object.hasOwn(descriptions,state)?descriptions[state]:'Your email preference could not be loaded. Refresh before making a change.'));
    const message=formMessage();message.dataset.yuMarketingMessage='';message.textContent=marketingNotice;card.append(message);
    if(action){
      const save=btn(action==='subscribe'?'Subscribe to the World of YŪ SEI':'Unsubscribe',()=>saveMarketingPreference(action),'yu-member-button yu-member-button--compact');
      save.disabled=Boolean(marketingWrite)||marketingNeedsRefresh||!config.allowWrites||config.designMode;
      if(marketingWrite)save.setAttribute('aria-busy','true');card.append(save);
    }
    if(marketingNeedsRefresh||!action||state==='PENDING'){
      const refresh=btn('Refresh preference',()=>withButton(refresh,async()=>{if(!marketingWrite)await loadCustomer();},message));
      refresh.disabled=Boolean(marketingWrite);card.append(refresh);
    }
    if(!action)card.append(helpLink('Contact YŪ SEI about email preferences','Email preferences'));
    root.append(card);
  }
  async function saveMarketingPreference(action) {
    if(action!=='subscribe'&&action!=='unsubscribe')return;
    if(marketingWrite||marketingNeedsRefresh||!config.allowWrites||config.designMode||marketingAction(customer?.emailAddress?.marketingState)!==action)return;
    const operation={generation,customerId:session?.customerId};
    if(!operation.customerId||customer?.id!==operation.customerId)return;
    const current=()=>marketingWrite===operation&&generation===operation.generation&&session?.customerId===operation.customerId;
    marketingWrite=operation;marketingRevision++;marketingNeedsRefresh=true;marketingNotice='Saving your email preference…';renderProfile();
    try {
      const key=action==='subscribe'?'customerEmailMarketingSubscribe':'customerEmailMarketingUnsubscribe';
      const result=await mutate(action==='subscribe'?Q.marketingSubscribe:Q.marketingUnsubscribe,{},key);
      if(!current())return;
      marketingRevision++;
      if(!result.emailAddress){const e=new Error('The email preference result could not be confirmed.');e.uncertain=true;throw e;}
      await loadCustomer();
      if(!current())return;
      const state=customer?.emailAddress?.marketingState;
      const confirmed=action==='subscribe'?state==='SUBSCRIBED'||state==='PENDING':state==='UNSUBSCRIBED'||state==='NOT_SUBSCRIBED';
      marketingNeedsRefresh=!confirmed;
      marketingNotice=confirmed?(state==='PENDING'?'Your subscription is pending. Check your email for any confirmation steps.':action==='subscribe'?'You are now subscribed to the World of YŪ SEI.':'You are now unsubscribed from the World of YŪ SEI.'):'Shopify’s current preference could not confirm this change. Refresh before trying again.';
    }catch(e){
      if(!current())return;
      marketingRevision++;marketingNeedsRefresh=true;marketingNotice=errorText(e)+' Refresh your email preference before trying again.';
    }finally{
      if(current()){
        marketingWrite=null;renderProfile();$('[data-yu-marketing-message]')?.focus();
      }
    }
  }
  function renderProfile(){
    if(!customer)return;const root=$('[data-yu-profile-content]');root.replaceChildren();root.dataset.yuPrivate='';writeNotice(root);
    const details=node('div',null,'yu-classic-profile-details'),addresses=node('div',null,'yu-classic-addresses');root.append(details,addresses);
    const card=node('div',null,'yu-member-profile-card yu-classic-card');card.append(heading('Personal information'));
    const form=node('form'),grid=node('div',null,'yu-account-form-grid');
    const first=field(grid,'firstName','First name',customer.firstName||'',{max:150,autocomplete:'given-name'}),last=field(grid,'lastName','Last name',customer.lastName||'',{max:150,autocomplete:'family-name'});
    const message=formMessage(),save=node('button','Save name','yu-member-button yu-member-button--compact');save.type='submit';save.disabled=!config.allowWrites;message.dataset.yuNameMessage='';message.textContent=nameSaveNotice;
    for(const input of [first,last])input.addEventListener('input',()=>{nameSaveNotice='';message.textContent='';});
    form.append(grid,message,save);card.append(form);details.append(card);
    const emailCard=node('div',null,'yu-member-profile-card yu-classic-card yu-classic-email');emailCard.append(heading('Sign-in email'),paragraph(customer.emailAddress?.emailAddress||'Not available'),helpLink('Request a sign-in email change','Sign-in email change'));details.append(emailCard);
    let busy=false;form.addEventListener('submit',e=>{e.preventDefault();if(busy||!config.allowWrites)return;busy=true;nameSaveNotice='';message.textContent='';const epoch=generation,expectedId=session?.customerId;withButton(save,async()=>{await mutate(Q.name,{input:{firstName:first.value.trim(),lastName:last.value.trim()}},'customerUpdate');if(epoch!==generation||session?.customerId!==expectedId)return;nameSaveNotice='Your name has been saved.';message.textContent=nameSaveNotice;try{await loadCustomer();}catch(_){if(epoch===generation&&session?.customerId===expectedId&&nameSaveNotice){nameSaveNotice='Your name has been saved. The display could not refresh. Refresh the account to see the latest record.';const current=root.querySelector('[data-yu-name-message]');if(current)current.textContent=nameSaveNotice;}}},message).finally(()=>busy=false);});
    renderMarketingPreference(details);
    const h=node('div',null,'yu-account-detail-heading');h.append(heading('Saved addresses'),btn('Add address',()=>addressForm(null,root),'yu-member-text-button'));addresses.append(h);
    const list=node('div',null,'yu-member-profile-grid');
    for(const a of customer.addresses.nodes){const c=node('div',null,'yu-member-profile-card');const isDefault=a.id===customer.defaultAddress?.id;c.append(heading(isDefault?'Default address':'Saved address',4));const address=node('address');for(const line of a.formatted||[])address.append(node('div',line));c.append(address);
      const actions=node('div',null,'yu-account-address-actions');actions.append(btn('Edit',()=>addressForm(a,root)));
      if(!isDefault){const set=btn('Make default',()=>withButton(set,async()=>{await mutate(Q.editAddress,{addressId:a.id,defaultAddress:true},'customerAddressUpdate');await afterConfirmedSave('Default address saved.');}));set.disabled=!config.allowWrites;actions.append(set);}
      const remove=btn('Remove',()=>confirmDelete(a,c));remove.disabled=!config.allowWrites;actions.append(remove);c.append(actions);list.append(c);
    }
    if(!customer.addresses.nodes.length)list.append(paragraph('No saved addresses yet.'));addresses.append(list);
  }
  function confirmDelete(a,card){
    if(card.querySelector('[data-yu-delete-confirm]'))return;
    const box=node('div',null,'yu-account-notice');box.dataset.yuDeleteConfirm='';box.append(paragraph('Remove this saved address? Existing order delivery addresses will not change.'));
    const message=formMessage(),remove=btn('Confirm removal',()=>withButton(remove,async()=>{await mutate(Q.deleteAddress,{addressId:a.id},'customerAddressDelete');card.remove();await afterConfirmedSave('Saved address removed.');},message));box.append(remove,btn('Keep address',()=>box.remove()),message);card.append(box);
  }
  const regionCodes='AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'.split(' ');
  function countries(){let names;try{names=new Intl.DisplayNames([config.locale||'en'],{type:'region'});}catch(_){}return regionCodes.map(c=>[c,names?.of(c)||c]).sort((a,b)=>a[1].localeCompare(b[1]));}
  async function mutateAddress(a,vars){
    if(!a&&addressCreateNeedsRefresh){const e=new Error('Refresh your account before adding another address.');e.uncertain=true;throw e;}
    try {return await mutate(a?Q.editAddress:Q.addAddress,vars,a?'customerAddressUpdate':'customerAddressCreate');}
    catch(e){if(!a&&e.uncertain)addressCreateNeedsRefresh=true;throw e;}
  }
  // The Add address modal is mounted outside the re-rendered profile content.
  // Sign-out explicitly closes it before clearing customer data.
  function closeAddressModal(restoreFocus=true, force=false){
    const active=activeAddressModal;
    if(!active || (!force && active.busy))return;
    activeAddressModal=null;
    if(active.dialog.open)active.dialog.close();
    active.dialog.remove();
    document.documentElement.classList.remove('yu-address-modal-open');
    if(restoreFocus && session && generation===active.epoch && session.customerId===active.customerId){
      const target=active.trigger?.isConnected ? active.trigger : space.querySelector('.yu-classic-addresses > .yu-account-detail-heading > button');
      target?.focus();
    }
  }
  function addressForm(a,root){
    // Use the same dialog for adding and editing. Unsupported browsers retain
    // the existing functional inline form.
    const modal=typeof HTMLDialogElement!=='undefined' && typeof HTMLDialogElement.prototype.showModal==='function';
    if(modal && activeAddressModal){activeAddressModal.dialog.focus();return;}
    root.querySelector('[data-yu-address-form]')?.remove();
    const box=node(modal?'dialog':'div',null,modal?'yu-account-address-modal':'yu-account-inline-form');box.dataset.yuAddressForm='';
    const title=heading(a?'Edit address':'Add address');
    if(modal){
      title.id='YuAddressModalTitle'+(++serial);box.setAttribute('aria-labelledby',title.id);box.setAttribute('aria-modal','true');
      const header=node('div',null,'yu-address-modal-heading'),close=node('button','×','yu-address-modal-close');
      close.type='button';close.setAttribute('aria-label','Close');close.addEventListener('click',()=>closeAddressModal());header.append(title,close);box.append(header);
    }else box.append(title);
    writeNotice(box);
    const form=node('form'),grid=node('div',null,'yu-account-form-grid');const inputs={};
    for(const [key,label,autocomplete,required] of [['firstName','First name','given-name',false],['lastName','Last name','family-name',false],['company','Company (optional)','organization',false],['address1','Address line 1','address-line1',true],['address2','Address line 2 (optional)','address-line2',false],['city','Town / city','address-level2',true],['zip','Postcode / ZIP','postal-code',false],['phoneNumber','Phone (optional, include country code)','tel',false]])inputs[key]=field(grid,key,label,a?.[key]||'',{autocomplete,required,max:250,type:key==='phoneNumber'?'tel':'text'});
    inputs.territoryCode=field(grid,'territoryCode','Country / region',a?.territoryCode||config.country||'GB',{choices:countries(),required:true,autocomplete:'country'});
    inputs.zoneCode=field(grid,'zoneCode','State / province code (when required)',a?.zoneCode||'',{max:20,autocomplete:'address-level1'});
    const hint=paragraph('For countries that require a state or province, use its code (for example CA for California or ON for Ontario). UK addresses can leave this blank.','yu-member-fineprint');
    const def=node('label',null,'yu-account-checkbox'),checkbox=node('input');checkbox.type='checkbox';checkbox.checked=a?.id===customer.defaultAddress?.id||!customer.addresses.nodes.length;def.append(checkbox,node('span','Use as my default address'));
    const message=formMessage(),save=node('button','Save address','yu-member-button yu-member-button--compact');save.type='submit';save.disabled=!config.allowWrites||(!a&&addressCreateNeedsRefresh);const actions=node('div',null,'yu-account-form-actions');actions.append(save,btn('Cancel',()=>modal?closeAddressModal():box.remove()));form.append(grid,hint,def,message,actions);box.append(form);
    let busy=false;
    if(modal){
      const trigger=document.activeElement,epoch=generation,customerId=session?.customerId;
      activeAddressModal={dialog:box,trigger,epoch,customerId,busy:false};
      box.addEventListener('cancel',e=>{e.preventDefault();closeAddressModal();});
      box.addEventListener('close',()=>{if(activeAddressModal?.dialog===box)closeAddressModal();});
      space.append(box);
      try {box.showModal();document.documentElement.classList.add('yu-address-modal-open');}
      catch(_){activeAddressModal=null;box.remove();status('The address form could not be opened. Please try again.',true);return;}
    }else (root.querySelector('.yu-classic-addresses')||root).append(box);
    inputs.firstName.focus();
    form.addEventListener('submit',e=>{e.preventDefault();if(busy||!config.allowWrites||(!a&&addressCreateNeedsRefresh))return;busy=true;if(modal&&activeAddressModal?.dialog===box)activeAddressModal.busy=true;
      const epoch=generation,customerId=session?.customerId;
      withButton(save,async()=>{
        const address={};for(const [k,v]of Object.entries(inputs))address[k]=v.value.trim();address.territoryCode=address.territoryCode.toUpperCase();address.zoneCode=address.zoneCode.toUpperCase()||null;
        const vars={address,defaultAddress:checkbox.checked};if(a)vars.addressId=a.id;
        await mutateAddress(a,vars);
        if(modal){
          if(generation!==epoch||session?.customerId!==customerId)return;
          closeAddressModal(false,true);
          await afterConfirmedSave('Your address has been saved.');
          if(generation===epoch&&session?.customerId===customerId)space.querySelector('.yu-classic-addresses > .yu-account-detail-heading > button')?.focus();
        }else {box.remove();await afterConfirmedSave('Your address has been saved.');}
      },message).finally(()=>{busy=false;if(modal&&activeAddressModal?.dialog===box)activeAddressModal.busy=false;});
    });
  }
  async function afterConfirmedSave(text){
    status(text);
    try {await loadCustomer();} catch (_) {status(text+' The display could not refresh. Refresh the account to see the latest record.');}
  }
  async function loadAccount(){
    ['[data-yu-orders-content]','[data-yu-profile-content]','[data-yu-returns-content]'].forEach(s=>$(s)?.replaceChildren(paragraph('Loading your account…')));
    const r=$('[data-yu-account-refresh]');if(r)r.hidden=false;
    status('Loading your account…');await loadCustomer();await loadOrders();if(addressCreateNeedsRefresh){addressCreateNeedsRefresh=false;renderProfile();}status(config.allowWrites?'':'Read-only test: profile saves and return submissions are locked.');
    await openAccountDestination();
  }
  $('[data-yu-account-refresh]')?.addEventListener('click',e=>withButton(e.currentTarget,loadAccount));
  document.addEventListener('click',e=>{
    const link=e.target instanceof Element?e.target.closest('[data-yu-member-login]'):null;
    if(!link||!config.enabled||!config.clientId||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey)return;
    e.preventDefault();if(session){if(destination)openAccountDestination();else space.selectTab('orders',true,true);return;}beginLogin(false);
  });
  window.addEventListener('pagehide',()=>{closeAddressModal(false,true);pageLeaving=true;peerReconnectPending=false;session=null;customer=null;orders=[];renderGreeting(false);inFlight.forEach(c=>c.abort());});
  window.addEventListener('online',()=>{if(!session)status('Connection restored. Sign in to continue.');});
  // Process the registered callback first; then display the reloadable page.
  // No navigation: customer tokens remain in memory. Never retain OAuth values.
  function canonicalAccountAddress() {
    if (config.designMode) return;
    const current = new URL(location.href);
    if (current.pathname !== '/search' || current.searchParams.get('view') !== 'yu-account') return;
    const clean = new URL('/pages/myaccount', location.origin);
    for (const key of ['yu_order', 'yu_order_name', 'yu_tab', 'yu_return', 'page', 'preview_theme_id']) {
      for (const value of current.searchParams.getAll(key)) clean.searchParams.append(key, value);
    }
    if (/^#(?:orders|returns|profile|addresses|help|overview|journal|sunday|paper)$/.test(current.hash)) clean.hash = current.hash;
    history.replaceState(history.state, '', clean.pathname + clean.search + clean.hash);
  }
  async function start(){
    if(!config.enabled||!config.clientId){if(config.previewNotice)status('API setup is not complete. Order details use the existing storefront session; name/address saves and formal return requests are not yet connected.');return;}
    if(config.designMode){status('Open this account in a separate storefront preview tab to test secure sign-in and live customer data.');return;}
    const logout=stored(LOGOUT);if(logout&&Date.now()-logout.at<300000){clearSignoutIntent();store(LOGOUT,null);store(RESUME,null);location.replace(config.logoutUrl||'/account/logout');return;}
    let callback=window.__yuAccountCallback;delete window.__yuAccountCallback;
    // Fallback for alternate layouts: remove callback parameters before use.
    const u=new URL(location.href);if(!callback&&(u.searchParams.has('code')||u.searchParams.has('error'))){callback={code:u.searchParams.get('code'),state:u.searchParams.get('state'),error:u.searchParams.get('error'),issuer:u.searchParams.get('iss')};['code','state','error','error_description','iss','session_state'].forEach(k=>u.searchParams.delete(k));history.replaceState(history.state,'',u.pathname+u.search+u.hash);}
    try{
      if(u.searchParams.get('sign_in')==='1')clearSignoutIntent();
      else signoutIntent=takeSignoutIntent();
      if(callback){
        status('Completing secure account request…');
        const verified=await exchange(callback);
        if(signoutIntent){
          checkedSignoutIntent(signoutIntent);
          if(verified||callback.error==='login_required'){await signout();return;}
          throw new Error('Shopify could not complete sign-out without further interaction. Please try Sign out again.');
        }
        canonicalAccountAddress();
        if(verified)await loadAccount();else renderSession(false);return;
      }
      const explicit=u.searchParams.get('sign_in')==='1';if(explicit){u.searchParams.delete('sign_in');history.replaceState(history.state,'',u.pathname+u.search+u.hash);await beginLogin(false);return;}
      if(signoutIntent){peerReconnectPending=false;await checkExistingSignIn();return;}
      // Preserve the server-confirmed contact outcome instead of redirecting it away.
      if(space.querySelector('[data-yu-support-success], [data-yu-support-errors], #YuAccountHelpForm .errors')){renderSession(false);space.selectTab('help',false,true);return;}
      // Exactly one top-level silent check for this entry, including fresh tabs.
      // The callback branch above terminates on login_required; it does NOT
      // fall through and retry. Explicit logout / contact outcomes stay earlier.
      await checkExistingSignIn();return;
    }catch(e){const signingOut=Boolean(signoutIntent);clearSignoutIntent();store(RESUME,null);clearPrivate();renderSession(false);status(signingOut?'Sign-out could not be completed. '+errorText(e):errorText(e),true);}
  }
  start().finally(()=>{startupSettled=true;resumeAfterPeerLogin();});
})();
